<template>
  <v-row>
    <v-col class="align-left">
      <ExpandCollapseToggle
        id="elementsPanelToggle"
        @toggleAllPanels="toggleAllPanels"
        styleClass="mt-3"
        :panel="panel"
      />
    </v-col>
    <v-col class="align-right mt-2" v-if="isManagement">
      <ValidationCountsHeader />
    </v-col>
  </v-row>
  <v-container fluid>
    <v-row v-if="inspectionPage === INSPECTION_PAGES.ELEMENTS">
      <v-col sm="12">
        <v-btn id="btn_addElement" variant="outlined" class="mr-5" @click="addElement" :disabled="!isEditable"
          >Add Element</v-btn
        >
        <v-btn id="btn_copyElement" variant="outlined" class="mx-5" @click="copyElement" :disabled="!isEditable"
          >Copy Element</v-btn
        >
        <v-tooltip>
          <template v-slot:activator="{ props }">
            <v-icon id="icon_copyElementInfo" class="ml-2 infoIcon" icon="fas fa-circle-info" v-bind="props" />
          </template>
          <span
            >Options available in Source Elem ID and Target Structure ID are filtered based on the selection of Source
            Structure Unit selected. Target Structure ID will not display any Structure IDs that already contain the
            Elem ID selected. Save button will be disabled until all three fields are successfully selected.
          </span>
        </v-tooltip>
        <v-btn
          id="btn_copyStructureUnit"
          variant="outlined"
          class="mx-5"
          @click="copyStructureUnit"
          :disabled="!isEditable"
          >Copy Structure Unit</v-btn
        >
        <v-tooltip>
          <template v-slot:activator="{ props }">
            <v-icon id="icon_copyStructureUnitInfo" class="ml-2 infoIcon" icon="fas fa-circle-info" v-bind="props" />
          </template>
          <span
            >The Target Structure Unit cannot have any elements defined. Save button will be disabled until both Source
            and Target Units are successfully selected.
          </span>
        </v-tooltip>
      </v-col>
    </v-row>
    <v-row>
      <v-col sm="12" class="flex-center pt-0">
        <v-radio-group id="radio_elements" v-model="elementQtyGroup" inline hide-details>
          <v-radio :value="ELEMENTS.QUANTITY" color="primary">
            <template v-slot:label> Quantity </template>
          </v-radio>
          <v-radio :value="ELEMENTS.PERCENTAGE" color="primary"><template v-slot:label> Percentage </template></v-radio>
        </v-radio-group>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-expansion-panels v-model="panel" multiple color="#fff">
          <v-expansion-panel
            v-for="(structureUnit, key, index) in structureUnitGroups"
            :key="structureUnit"
            :id="'expansion_structure_' + index"
            :value="'structureUnit_' + index"
          >
            <v-expansion-panel-title
              class="d-flex justify-start"
              hide-actions
              expand-icon="fa-solid fa-plus"
              collapse-icon="fa-solid fa-minus"
            >
              <v-icon v-if="panel.includes('structureUnit_' + index)" icon="fa-solid fa-minus" />
              <v-icon v-else icon="fa-solid fa-plus" />

              <v-row>
                <v-col>
                  <h3>Structure Unit ID:&nbsp;{{ structureUnit.strUnitLabel }}</h3>
                </v-col>
                <v-col>
                  <h3>
                    Type:&nbsp;{{
                      configStore.getReferenceValue(
                        REFERENCE_TABLE.STRUCTURE_UNIT_TYPE,
                        structureUnit.legacyStructureType,
                      )
                    }}
                  </h3>
                </v-col>
                <v-col>
                  <h3>Key:&nbsp;{{ structureUnit.strUnitKey }}</h3>
                </v-col>
              </v-row>
            </v-expansion-panel-title>

            <v-expansion-panel-text>
              <v-label
                class="noRecordsMessage d-flex justify-center"
                v-if="!structureUnit?.elements || structureUnit?.elements?.length == 0"
                >This structure unit currently contains no elements.
              </v-label>
              <v-table v-else class="compactTable">
                <thead>
                  <tr>
                    <th id="th_empty" class="table-cell_width__expansion">&nbsp;</th>
                    <th id="th_elem" class="table-cell_width__desc table--row__align-top">
                      <div class="align-center">B.E.01 <br />Element Description</div>
                    </th>

                    <th
                      id="th_group"
                      v-if="getStructureType(elementsValue.structureBridge?.servTypOn) === 'T'"
                      class="table--row__align-top"
                    >
                      <div class="align-center"><br />E.07 Group/Unit</div>
                    </th>
                    <th id="th_qty" class="table--row__align-top">
                      <div class="align-center">B.E.03 <br />Total <br />Qty</div>
                    </th>
                    <th id="th_uom" class="table--row__align-top">
                      <div class="align-center">E.01 <br />UOM</div>
                    </th>
                    <th id="th_pct1" v-if="elementQtyGroup === ELEMENTS.PERCENTAGE" class="table--row__align-top">
                      <div class="align-center">
                        B.CS.01<br />
                        CS1 Pct
                      </div>
                    </th>
                    <th id="th_qty1" v-else class="table--row__align-top">
                      <div class="align-center">B.CS.01 <br />CS1 <br />Qty</div>
                    </th>
                    <th id="th_pct2" v-if="elementQtyGroup === ELEMENTS.PERCENTAGE" class="table--row__align-top">
                      <div class="align-center">
                        B.CS.02<br />
                        CS2 Pct
                      </div>
                    </th>
                    <th id="th_qty2" v-else class="table--row__align-top">
                      <div class="align-center">
                        B.CS.02<br />
                        CS2 <br />Qty
                      </div>
                    </th>
                    <th id="th_pct3" v-if="elementQtyGroup === ELEMENTS.PERCENTAGE" class="table--row__align-top">
                      <div class="align-center">
                        B.CS.03<br />
                        CS3 Pct
                      </div>
                    </th>
                    <th id="th_qty3" v-else class="table--row__align-top">
                      <div class="align-center">
                        B.CS.03<br />
                        CS3 <br />Qty
                      </div>
                    </th>
                    <th id="th_pct4" v-if="elementQtyGroup === ELEMENTS.PERCENTAGE" class="table--row__align-top">
                      <div class="align-center">
                        B.CS.04<br />
                        CS4 Pct
                      </div>
                    </th>
                    <th id="th_qty4" v-else class="table--row__align-top">
                      <div class="align-center">B.CS.04 <br />CS4 <br />Qty</div>
                    </th>

                    <th id="th_action" class="table--row__align-top">
                      <div class="align-center"><br />Action</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(element, index) in structureUnit.elements" :key="element">
                    <tr :id="'tr_' + index" class="element">
                      <td class="compactCell table-cell_width__expansion">
                        <v-icon
                          v-if="
                            !elementsOpened.includes(`${structureUnit.strUnitKey}-${element.elemKey}`) &&
                            element?.children?.length > 0
                          "
                          icon="fa-sharp fa-solid fa-chevron-right"
                          @click="toggle(`${structureUnit.strUnitKey}-${element.elemKey}`)"
                        />
                        <v-icon
                          v-if="
                            elementsOpened.includes(`${structureUnit.strUnitKey}-${element.elemKey}`) &&
                            element?.children?.length > 0
                          "
                          icon="fa-solid fa-chevron-down"
                          @click="toggle(`${structureUnit.strUnitKey}-${element.elemKey}`)"
                        />
                      </td>
                      <InspectionElementRow
                        :element="element"
                        :structureType="getStructureType(elementsValue.structureBridge?.servTypOn)"
                        :elementQtyGroup="elementQtyGroup"
                      />

                      <td style="white-space: nowrap">
                        <div v-if="inspectionPage === INSPECTION_PAGES.ELEMENTS">
                          <v-icon
                            :icon="isEditable ? 'fa-solid fa-pencil' : 'fa-solid fa-search'"
                            @click="
                              {
                                editUnit(element);
                                showElementDialog.show = true;
                              }
                            "
                          />
                          &nbsp;&nbsp;
                          <v-icon
                            v-if="isEditable"
                            icon="fa-solid fa-trash"
                            @click="deleteUnit(element, structureUnit.strUnitKey)"
                          />
                        </div>
                        <div v-else-if="inspectionPage === INSPECTION_PAGES.TUNNELS">
                          <v-icon
                            icon="fa-solid fa-search"
                            @click="
                              {
                                editUnit(element);
                                showElementDialog.show = true;
                              }
                            "
                          />
                        </div>
                      </td>
                    </tr>
                    <template v-for="(defect, idx) in element.children" :key="defect">
                      <tr
                        :id="'tr_defect_' + idx"
                        :class="getColor(configStore.getElementDefinition(defect.elemKey))"
                        v-if="elementsOpened.includes(`${structureUnit.strUnitKey}-${element.elemKey}`)"
                      >
                        <td class="compactCell">
                          <span v-if="defect?.children?.length > 0" style="padding-left: 1rem"></span>
                          <v-icon
                            v-if="
                              defect?.children?.length > 0 &&
                              !protectiveSystemselementsOpened?.includes(
                                `${structureUnit.strUnitKey}-${element.elemKey}-${idx}`,
                              )
                            "
                            icon="fa-sharp fa-solid fa-chevron-right"
                            @click="toggleProtectiveSystems(`${structureUnit.strUnitKey}-${element.elemKey}-${idx}`)"
                          />
                          <v-icon
                            v-if="
                              defect?.children?.length > 0 &&
                              protectiveSystemselementsOpened?.includes(
                                `${structureUnit.strUnitKey}-${element.elemKey}-${idx}`,
                              )
                            "
                            icon="fa-solid fa-chevron-down"
                            @click="toggleProtectiveSystems(`${structureUnit.strUnitKey}-${element.elemKey}-${idx}`)"
                          />
                          <span v-if="defect?.children?.length > 0" style="padding-left: 0.2rem"></span>
                          <span v-else style="padding-left: 1rem"> </span>

                          <v-icon
                            v-if="elementsOpened.includes(`${structureUnit.strUnitKey}-${element.elemKey}`)"
                            :icon="getIcon(configStore.getElementDefinition(defect.elemKey))"
                          />
                        </td>
                        <InspectionElementRow
                          :element="defect"
                          :structureType="getStructureType(elementsValue.structureBridge?.servTypOn)"
                          :elementQtyGroup="elementQtyGroup"
                        />

                        <td style="white-space: nowrap">
                          <v-icon
                            v-if="getIcon(configStore.getElementDefinition(defect.elemKey)) === 'fa-solid no-outline'"
                            :icon="isEditable ? 'fa-solid fa-pencil' : 'fa-solid fa-search'"
                            @click="
                              {
                                editUnit(defect);
                                showElementDialog.show = true;
                              }
                            "
                          />
                          &nbsp;&nbsp;
                          <v-icon
                            v-if="
                              isEditable &&
                              getIcon(configStore.getElementDefinition(defect.elemKey)) === 'fa-solid no-outline'
                            "
                            icon="fa-solid fa-trash"
                            @click="deleteUnit(defect, defect.strUnitKey)"
                          />
                        </td>
                      </tr>
                      <template v-for="(protectSystemDefect, i) in defect?.children" :key="protectSystemDefect">
                        <tr
                          :class="getColor(configStore.getElementDefinition(protectSystemDefect.elemKey))"
                          :id="'tr_protectiveSystemDefect_' + i"
                          v-if="
                            protectiveSystemselementsOpened.includes(
                              `${structureUnit.strUnitKey}-${element.elemKey}-${idx}`,
                            )
                          "
                        >
                          <td class="compactCell">
                            <span v-if="protectSystemDefect?.children?.length > 0" style="padding-left: 2rem"></span>
                            <v-icon
                              v-if="
                                protectSystemDefect?.children?.length > 0 &&
                                !functionalElementsOpened?.includes(
                                  `${structureUnit.strUnitKey}-${element.elemKey}-${idx}-${i}`,
                                )
                              "
                              icon="fa-sharp fa-solid fa-chevron-right"
                              @click="
                                toggleFunctionalElements(`${structureUnit.strUnitKey}-${element.elemKey}-${idx}-${i}`)
                              "
                            />
                            <v-icon
                              v-if="
                                protectSystemDefect?.children?.length > 0 &&
                                functionalElementsOpened?.includes(
                                  `${structureUnit.strUnitKey}-${element.elemKey}-${idx}-${i}`,
                                )
                              "
                              icon="fa-solid fa-chevron-down"
                              @click="
                                toggleFunctionalElements(`${structureUnit.strUnitKey}-${element.elemKey}-${idx}-${i}`)
                              "
                            />
                            <span v-if="protectSystemDefect?.children?.length > 0" style="padding-left: 0.2rem"></span>
                            <span v-else style="padding-left: 3rem"> </span>
                            <v-icon :icon="getIcon(configStore.getElementDefinition(protectSystemDefect.elemKey))" />
                          </td>
                          <InspectionElementRow
                            :element="protectSystemDefect"
                            :structureType="getStructureType(elementsValue.structureBridge?.servTypOn)"
                            :elementQtyGroup="elementQtyGroup"
                          />
                          <td></td>
                        </tr>
                        <template
                          v-for="fnSystemProtectiveSys in protectSystemDefect?.children"
                          :key="fnSystemProtectiveSys"
                        >
                          <tr
                            v-if="
                              functionalElementsOpened?.includes(
                                `${structureUnit.strUnitKey}-${element.elemKey}-${idx}-${i}`,
                              )
                            "
                            :class="getColor(configStore.getElementDefinition(fnSystemProtectiveSys.elemKey))"
                          >
                            <td>
                              <v-icon
                                style="padding-left: 4.2rem"
                                :icon="getIcon(configStore.getElementDefinition(fnSystemProtectiveSys.elemKey))"
                              />
                            </td>
                            <InspectionElementRow
                              :element="fnSystemProtectiveSys"
                              :structureType="getStructureType(elementsValue.structureBridge?.servTypOn)"
                              :elementQtyGroup="elementQtyGroup"
                            />

                            <td></td>
                          </tr>
                        </template>
                      </template>
                    </template>
                  </template>
                </tbody>
              </v-table>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
  </v-container>
  <!--Add or Edit Element Dialog -->
  <v-dialog v-model="showElementDialog.show" scrollable persistent class="dialogForm">
    <v-card>
      <v-card-title>Element Selector</v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-form v-model="valid" ref="modifyElementForm">
          <v-row>
            <v-col sm="12" md="6">
              <LabelSelect
                :labelXl="3"
                :labelLg="4"
                :labelMd="5"
                :labelSm="4"
                :selectXl="8"
                :selectLg="7"
                :selectMd="6"
                :selectSm="8"
                id="structureUnit"
                label="E.02 Structure Unit"
                :options="structureUnitTypes"
                :isEditable="isEditable && showElementDialog.isAdd"
                v-model="selectedElement.strUnitKey"
                :isRequired="isEditable"
                @update:modelValue="
                  {
                    resetAddElementForUnitKeyChange(selectedElement.strUnitKey);
                  }
                "
              ></LabelSelect>
            </v-col>
            <v-col sm="12" md="6">
              <LabelSelect
                :labelXl="3"
                :labelLg="4"
                :labelMd="5"
                :labelSm="4"
                :selectXl="8"
                :selectLg="7"
                :selectMd="6"
                :selectSm="8"
                id="elemDescription"
                label="B.E.01 Element ID"
                :options="getUniqueElements(selectedElement.strUnitKey)"
                :isEditable="isEditable && showElementDialog.isAdd"
                v-model="selectedElement.elemKey"
                :isRequired="isEditable"
                @update:modelValue="
                  {
                    getDefectsAndProtectiveSystems(selectedElement.elemKey);
                    changesDetected = true;
                  }
                "
              ></LabelSelect>

              <InputMessage :showMessage="!!noFunctionalSystemMessage" :message="noFunctionalSystemMessage" />
            </v-col>
          </v-row>
          <v-row v-if="$vuetify.display.mdAndUp">
            <v-col sm="6">
              <v-row v-if="getStructureType(elementsValue.structureBridge?.servTypOn) === 'T'">
                <v-col sm="4">
                  <LabelText
                    noGrid
                    id="elemTag"
                    label="E.07 Group/Unit"
                    v-model="selectedElement.elemTag"
                    :isEditable="isEditable"
                    :counter="null"
                  >
                  </LabelText>
                </v-col>
                <v-col sm="4" class="d-flex">
                  <LabelText
                    noGrid
                    id="envKey"
                    label="E.03 Environment"
                    :refTable="REFERENCE_TABLE.ENVIRONMENT"
                    v-model="selectedElement.envKey"
                    class="text_noPaddingLeft"
                  >
                  </LabelText>
                </v-col>
                <v-col sm="4" class="d-flex">
                  <LabelText noGrid id="uom" label="E.01 UOM" v-model="selectedElement.uom" class="text_noPaddingLeft">
                  </LabelText>
                </v-col>
              </v-row>
              <v-row v-else>
                <v-col sm="6" class="d-flex">
                  <LabelText
                    noGrid
                    id="envKey"
                    label="E.03 Environment"
                    :refTable="REFERENCE_TABLE.ENVIRONMENT"
                    v-model="selectedElement.envKey"
                  >
                  </LabelText>
                </v-col>
                <v-col sm="6" class="d-flex">
                  <LabelText noGrid id="uom" label="E.01 UOM" v-model="selectedElement.uom"> </LabelText>
                </v-col>
              </v-row>
            </v-col>
            <v-col>
              <v-row>
                <v-col sm="6">
                  <LabelText
                    noGrid
                    id="quantity"
                    label="B.E.03 Quantity"
                    v-model="selectedElement.elemQuantity"
                    type="number"
                    :allowCommas="false"
                    :isRequired="isEditable"
                    :maxlength="quantityCountMaxLength"
                    @input="updateDefectCount(null)"
                    :isEditable="isEditable"
                    textStyle="width:50%"
                    :rules="quantityRules"
                    @update:modelValue="changesDetected = true"
                  >
                  </LabelText>
                </v-col>
                <v-col sm="6">
                  <LabelText
                    noGrid
                    id="sf"
                    label="E.04 Scale Factor"
                    v-model="selectedElement.elemScaleFactor"
                    type="number"
                    :isRequired="isEditable"
                    :maxlength="10"
                    @input="updateDefectCount(null)"
                    :isEditable="isEditable"
                    :decimalPlaces="1"
                    :rules="
                      isEditable
                        ? [
                            (value) => {
                              return lessThanValueRule(value, 9999999.9);
                            },
                          ]
                        : []
                    "
                    textStyle="width:40%"
                    @update:modelValue="changesDetected = true"
                  >
                  </LabelText>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row v-else>
            <v-col sm="6" class="d-flex">
              <LabelText
                :labelSm="8"
                :textSm="4"
                id="envKey"
                label="E.03 Environment"
                :refTable="REFERENCE_TABLE.ENVIRONMENT"
                v-model="selectedElement.envKey"
                textStyle="width:100%"
              >
              </LabelText>
            </v-col>
            <v-col sm="6">
              <LabelText
                :labelSm="5"
                :textSm="7"
                id="elemTag"
                label="E.07 Group/Unit"
                v-model="selectedElement.elemTag"
                :isEditable="isEditable"
                :counter="null"
                v-if="getStructureType(elementsValue.structureBridge?.servTypOn) === 'T'"
              >
              </LabelText>
            </v-col>
            <v-col sm="8">
              <LabelText
                :labelSm="6"
                :textSm="6"
                id="quantity"
                label="B.E.03 Quantity"
                :allowCommas="false"
                v-model="selectedElement.elemQuantity"
                type="number"
                :isRequired="isEditable"
                :maxlength="quantityCountMaxLength"
                @input="updateDefectCount(null)"
                :isEditable="isEditable"
                textStyle="width:100%"
                :rules="quantityRules"
              >
              </LabelText>
            </v-col>
            <v-col sm="4" class="d-flex">
              <LabelText
                :labelSm="8"
                :textSm="4"
                id="uom"
                label="Unit/Measure"
                v-model="selectedElement.uom"
                textStyle="width:100%"
                class="text_noPaddingLeft"
              >
              </LabelText>
            </v-col>
            <v-col sm="12">
              <LabelText
                :labelSm="4"
                :textSm="4"
                id="sf"
                label="E.04 Scale Factor"
                v-model="selectedElement.elemScaleFactor"
                type="number"
                :isRequired="isEditable"
                :maxlength="10"
                @input="updateDefectCount(null)"
                :isEditable="isEditable"
                :decimalPlaces="1"
                textStyle="width:100%"
                :rules="
                  isEditable
                    ? [
                        (value) => {
                          return lessThanValueRule(value, 9999999.99);
                        },
                      ]
                    : []
                "
              >
              </LabelText>
            </v-col>
          </v-row>
          <v-row>
            <v-col colspan="2">
              <LabelText
                labelXl="12"
                labelLg="12"
                labelMd="12"
                labelSm="12"
                textXl="12"
                textLg="12"
                textMd="12"
                textSm="12"
                type="textarea"
                label="E.05 Element Description"
                id="elemDesc"
                v-model="selectedElement.elemDescription"
                :isEditable="isEditable"
                :counter="elemDescriptionMaxLength"
                :maxlength="elemDescriptionMaxLength"
                @update:modelValue="changesDetected = true"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col colspan="2">
              <LabelText
                labelXl="12"
                labelLg="12"
                labelMd="12"
                labelSm="12"
                textXl="12"
                textLg="12"
                textMd="12"
                textSm="12"
                type="textarea"
                label="E.06 Elem Condition"
                id="replacementComment"
                v-model="selectedElement.elemNotes"
                :isEditable="isEditable"
                :counter="elemConditionMaxLength"
                :maxlength="elemConditionMaxLength"
                @update:modelValue="changesDetected = true"
              />
            </v-col>
          </v-row>
          <v-row v-if="$vuetify.display.lgAndUp" class="elementsInput">
            <v-col cols="2" lg="2" class="mr-0 pr-0">
              <v-radio-group v-model="updateElementQtyGroup" hide-details density="compact">
                <v-radio :value="ELEMENTS.QUANTITY" color="primary" class="font-weight-bold"
                  ><template v-slot:label> Quantity </template></v-radio
                >
                <v-radio :value="ELEMENTS.PERCENTAGE" color="primary" class="font-weight-bold"
                  ><template v-slot:label> Percentage </template></v-radio
                >
              </v-radio-group></v-col
            >
            <v-col class="ml-0 pl-0" cols="10" lg="10">
              <v-row>
                <template v-if="updateElementQtyGroup === ELEMENTS.QUANTITY">
                  <v-col cols="3" lg="3">
                    <LabelText
                      noGrid
                      id="cs1Qty"
                      label="B.CS.01 CS1 Qty"
                      v-model="selectedElement.elemQtyState1"
                      textStyle="background-color:green;width:96px;margin-left:8px;margin-right:5px;padding-right:5px;padding-left: 0 !important;"
                      :rules="[greaterThanZeroRule]"
                      alignRight
                      readOnlyValidation
                      type="number"
                    >
                    </LabelText>
                  </v-col>
                </template>
                <template v-else>
                  <v-col cols="3">
                    <LabelText
                      noGrid
                      id="cs1Pct"
                      label="B.CS.01 Pct"
                      v-model="selectedElement.elemPctState1"
                      textStyle="background-color:green;width:96px;margin-left:8px;margin-right:5px;padding-right:5px;"
                      :rules="[greaterThanZeroRule]"
                      type="number"
                      readOnlyValidation
                      :decimalPlaces="2"
                      alignRight
                    >
                    </LabelText>
                  </v-col>
                </template>

                <template v-if="updateElementQtyGroup === ELEMENTS.QUANTITY">
                  <v-col cols="3" lg="3">
                    <LabelText
                      noGrid
                      id="cs2Qty"
                      label="B.CS.02 CS2 Qty"
                      v-model="selectedElement.elemQtyState2"
                      textStyle="background-color:yellow;width:96px;margin-left:8px;margin-right:5px;padding-right:5px;"
                      :rules="[greaterThanZeroRule]"
                      readOnlyValidation
                      type="number"
                      alignRight
                    >
                    </LabelText>
                  </v-col>
                </template>
                <template v-else>
                  <v-col cols="3" lg="3">
                    <LabelText
                      noGrid
                      id="cs2Pct"
                      label="B.CS.02 Pct"
                      v-model="selectedElement.elemPctState2"
                      textStyle="background-color:yellow;width:96px;margin-left:8px;margin-right:5px;padding-right:5px;"
                      :rules="[greaterThanZeroRule]"
                      readOnlyValidation
                      type="number"
                      :decimalPlaces="2"
                      alignRight
                    >
                    </LabelText>
                  </v-col>
                </template>
                <template v-if="updateElementQtyGroup === ELEMENTS.QUANTITY">
                  <v-col cols="3" lg="3">
                    <LabelText
                      noGrid
                      id="cs3Qty"
                      label="B.CS.03 CS3 Qty"
                      v-model="selectedElement.elemQtyState3"
                      textStyle="background-color:orange;width:100%;width:96px;margin-left:8px;margin-right:5px;padding-right:5px;"
                      :rules="[greaterThanZeroRule]"
                      readOnlyValidation
                      type="number"
                      alignRight
                    >
                    </LabelText>
                  </v-col>
                </template>
                <template v-else>
                  <v-col cols="3" lg="3">
                    <LabelText
                      noGrid
                      id="cs3Pct"
                      label="B.CS.03 Pct"
                      v-model="selectedElement.elemPctState3"
                      textStyle="background-color:orange;width:100%;width:96px;margin-left:8px;margin-right:5px;padding-right:5px;"
                      :rules="[greaterThanZeroRule]"
                      type="number"
                      readOnlyValidation
                      :decimalPlaces="2"
                      alignRight
                    >
                    </LabelText>
                  </v-col>
                </template>
                <template v-if="updateElementQtyGroup === ELEMENTS.QUANTITY">
                  <v-col cols="3" lg="3">
                    <LabelText
                      noGrid
                      id="cs4Qty"
                      label="B.CS.04 CS4 Qty"
                      v-model="selectedElement.elemQtyState4"
                      textStyle="background-color:red;width:96px;margin-left:8px;margin-right:5px;padding-right:5px;"
                      :rules="[greaterThanZeroRule]"
                      readOnlyValidation
                      type="number"
                      alignRight
                    >
                    </LabelText>
                  </v-col>
                </template>
                <template v-else>
                  <v-col cols="3" lg="3">
                    <LabelText
                      noGrid
                      id="cs4Pct"
                      label="B.CS.04 Pct"
                      v-model="selectedElement.elemPctState4"
                      textStyle="background-color:red;width:96px;margin-left:8px;margin-right:5px;padding-right:5px;"
                      :rules="[greaterThanZeroRule]"
                      type="number"
                      :decimalPlaces="2"
                      readOnlyValidation
                      alignRight
                    >
                    </LabelText>
                  </v-col>
                </template>
              </v-row>
            </v-col>
            <v-col cols="12" sm="12" class="pt-1 pl-0">
              <ExpandCollapseToggle
                id="approachPanelToggle"
                @toggleAllPanels="toggleAllPopupPanels"
                styleClass="ml-3"
                :panel="elementsPanel"
              />
            </v-col>
          </v-row>
          <v-row v-else class="elementsInput">
            <v-col cols="12" sm="12" class="py-0 pl-0">
              <v-radio-group v-model="updateElementQtyGroup" inline hide-details>
                <v-radio :value="ELEMENTS.QUANTITY" color="primary" class="font-weight-bold"
                  ><template v-slot:label> Quantity </template></v-radio
                >
              </v-radio-group></v-col
            >
            <v-col cols="12" sm="12" class="py-0 pl-0">
              <v-radio-group v-model="updateElementQtyGroup" inline hide-details>
                <v-radio :value="ELEMENTS.PERCENTAGE" color="primary" class="font-weight-bold"
                  ><template v-slot:label> Percentage </template></v-radio
                >
              </v-radio-group></v-col
            >
            <v-col cols="12" sm="12" class="py-0 pl-0">
              <template v-if="updateElementQtyGroup === ELEMENTS.QUANTITY">
                <v-col cols="12" sm="12" md="4" lg="3" class="pb-2 pt-1">
                  <LabelText
                    :labelXl="5"
                    :labelLg="6"
                    :labelMd="6"
                    :labelSm="3"
                    :textXl="6"
                    :textLg="5"
                    :textMd="5"
                    :textSm="3"
                    id="cs1Qty"
                    label="B.CS.01 Qty"
                    v-model="selectedElement.elemQtyState1"
                    textStyle="background-color:green;width:100%;padding-right:5px"
                    :rules="[greaterThanZeroRule]"
                    alignRight
                    readOnlyValidation
                    type="number"
                  >
                  </LabelText>
                </v-col>
              </template>
              <template v-else>
                <v-col cols="12" sm="12" md="4" lg="3" class="pb-2 pt-1">
                  <LabelText
                    :labelXl="5"
                    :labelLg="6"
                    :labelMd="6"
                    :labelSm="3"
                    :textXl="6"
                    :textLg="5"
                    :textMd="5"
                    :textSm="3"
                    id="cs1Pct"
                    label="B.CS.01 Pct"
                    v-model="selectedElement.elemPctState1"
                    textStyle="background-color:green;width:100%;padding-right:5px"
                    :rules="[greaterThanZeroRule]"
                    type="number"
                    readOnlyValidation
                    :decimalPlaces="2"
                    alignRight
                  >
                  </LabelText>
                </v-col>
              </template>

              <template v-if="updateElementQtyGroup === ELEMENTS.QUANTITY">
                <v-col cols="12" sm="12" md="4" lg="3" class="pb-2 pt-0">
                  <LabelText
                    :labelXl="5"
                    :labelLg="6"
                    :labelMd="6"
                    :labelSm="3"
                    :textXl="6"
                    :textLg="5"
                    :textMd="5"
                    :textSm="3"
                    id="cs2Qty"
                    label="B.CS.02 Qty"
                    v-model="selectedElement.elemQtyState2"
                    textStyle="background-color:yellow;width:100%;padding-right:5px"
                    :rules="[greaterThanZeroRule]"
                    readOnlyValidation
                    type="number"
                    alignRight
                  >
                  </LabelText>
                </v-col>
              </template>
              <template v-else>
                <v-col cols="12" sm="12" md="4" lg="3" class="pb-2 pt-0">
                  <LabelText
                    :labelXl="5"
                    :labelLg="6"
                    :labelMd="6"
                    :labelSm="3"
                    :textXl="6"
                    :textLg="5"
                    :textMd="5"
                    :textSm="3"
                    id="cs2Pct"
                    label="B.CS.02 Pct"
                    v-model="selectedElement.elemPctState2"
                    textStyle="background-color:yellow;width:100%;padding-right:5px"
                    :rules="[greaterThanZeroRule]"
                    readOnlyValidation
                    type="number"
                    :decimalPlaces="2"
                    alignRight
                  >
                  </LabelText>
                </v-col>
              </template>
              <template v-if="updateElementQtyGroup === ELEMENTS.QUANTITY">
                <v-col cols="12" sm="12" md="4" lg="3" class="pb-2 pt-0">
                  <LabelText
                    :labelXl="5"
                    :labelLg="6"
                    :labelMd="6"
                    :labelSm="3"
                    :textXl="6"
                    :textLg="5"
                    :textMd="5"
                    :textSm="3"
                    id="cs3Qty"
                    label="B.CS.03 Qty"
                    v-model="selectedElement.elemQtyState3"
                    textStyle="background-color:orange;width:100%;padding-right:5px"
                    :rules="[greaterThanZeroRule]"
                    readOnlyValidation
                    type="number"
                    alignRight
                  >
                  </LabelText>
                </v-col>
              </template>
              <template v-else>
                <v-col cols="12" sm="12" md="4" lg="3" class="pb-2 pt-0">
                  <LabelText
                    :labelXl="5"
                    :labelLg="6"
                    :labelMd="6"
                    :labelSm="3"
                    :textXl="6"
                    :textLg="5"
                    :textMd="5"
                    :textSm="3"
                    id="cs3Pct"
                    label="B.CS.03 Pct"
                    v-model="selectedElement.elemPctState3"
                    textStyle="background-color:orange;width:100%;padding-right:5px"
                    :rules="[greaterThanZeroRule]"
                    type="number"
                    readOnlyValidation
                    :decimalPlaces="2"
                    alignRight
                  >
                  </LabelText>
                </v-col>
              </template>
              <template v-if="updateElementQtyGroup === ELEMENTS.QUANTITY">
                <v-col cols="12" sm="12" md="4" lg="3" class="pb-2 pt-0">
                  <LabelText
                    :labelXl="5"
                    :labelLg="6"
                    :labelMd="6"
                    :labelSm="3"
                    :textXl="6"
                    :textLg="5"
                    :textMd="5"
                    :textSm="3"
                    id="cs4Qty"
                    label="B.CS.04 Qty"
                    v-model="selectedElement.elemQtyState4"
                    textStyle="background-color:red;width:100%;padding-right:5px"
                    :rules="[greaterThanZeroRule]"
                    readOnlyValidation
                    type="number"
                    alignRight
                  >
                  </LabelText>
                </v-col>
              </template>
              <template v-else>
                <v-col cols="12" sm="12" md="4" lg="3" class="pb-2 pt-0">
                  <LabelText
                    :labelXl="5"
                    :labelLg="6"
                    :labelMd="6"
                    :labelSm="3"
                    :textXl="6"
                    :textLg="5"
                    :textMd="5"
                    :textSm="3"
                    id="cs4Pct"
                    label="B.CS.04 Pct"
                    v-model="selectedElement.elemPctState4"
                    textStyle="background-color:red;width:100%;padding-right:5px"
                    :rules="[greaterThanZeroRule]"
                    type="number"
                    :decimalPlaces="2"
                    readOnlyValidation
                    alignRight
                  >
                  </LabelText>
                </v-col>
              </template>
            </v-col>
            <v-col cols="12" sm="12" class="pt-1 pl-0">
              <ExpandCollapseToggle
                id="approachPanelToggle"
                @toggleAllPanels="toggleAllPopupPanels"
                styleClass="ml-3"
                :panel="elementsPanel"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="12">
              <v-expansion-panels v-model="elementsPanel" multiple color="#fff">
                <v-expansion-panel id="panel_defects" value="defects">
                  <v-expansion-panel-title
                    class="d-flex justify-start"
                    hide-actions
                    expand-icon="fa-solid fa-plus"
                    collapse-icon="fa-solid fa-minus"
                  >
                    <v-icon v-if="elementsPanel.includes('defects')" icon="fa-solid fa-minus" />
                    <v-icon v-else icon="fa-solid fa-plus" />
                    <h3>Defects</h3>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <v-table class="w-100 compactTable elementsTable">
                      <thead>
                        <tr>
                          <th style="width: 7%" id="th_defect_applied"><br />Applied</th>
                          <th
                            style="width: 23%"
                            id="th_defect_desc"
                            class="dialog-table-cell_width__desc table--row__align-top"
                          >
                            <div class="align-center">
                              B.E.01<br />
                              Element Description
                            </div>
                          </th>
                          <th style="width: 10%" id="th_defect_qty" class="table--row__align-top">
                            <div class="align-center">
                              CS.01<br />
                              Qty
                            </div>
                          </th>
                          <th style="width: 10%" id="th_defect_sf" class="align-center">CS.02<br />SF</th>
                          <th id="th_defect_uom" style="width: 8%" class="align-center">E.01 <br />UOM</th>
                          <th
                            style="width: 10%"
                            id="th_defect_pct1"
                            v-if="updateElementQtyGroup === ELEMENTS.PERCENTAGE"
                            class="table--row__align-top"
                          >
                            <div class="align-center">
                              B.CS.01<br />
                              CS1 Pct
                            </div>
                          </th>
                          <th style="width: 10%" id="th_defect_qty1" v-else class="table--row__align-top">
                            <div class="align-center">
                              B.CS.01<br />
                              CS1 Qty
                            </div>
                          </th>
                          <th
                            style="width: 10%"
                            id="th_defect_pct2"
                            v-if="updateElementQtyGroup === ELEMENTS.PERCENTAGE"
                            class="table--row__align-top"
                          >
                            <div class="align-center">
                              B.CS.02<br />
                              CS2 Pct
                            </div>
                          </th>
                          <th style="width: 10%" id="th_defect_qty2" v-else class="table--row__align-top">
                            <div class="align-center">
                              B.CS.02<br />
                              CS2 Qty
                            </div>
                          </th>
                          <th
                            style="width: 10%"
                            id="th_defect_pct3"
                            v-if="updateElementQtyGroup === ELEMENTS.PERCENTAGE"
                            class="table--row__align-top"
                          >
                            <div class="align-center">B.CS.03 <br />CS3 Pct</div>
                          </th>
                          <th style="width: 10%" id="th_defect_qty3" v-else class="table--row__align-top">
                            <div class="align-center">
                              B.CS.03<br />
                              CS3 Qty
                            </div>
                          </th>
                          <th
                            style="width: 10%"
                            id="th_defect_pct4"
                            v-if="updateElementQtyGroup === ELEMENTS.PERCENTAGE"
                            class="table--row__align-top"
                          >
                            <div class="align-center">
                              B.CS.04<br />
                              CS4 Pct
                            </div>
                          </th>
                          <th style="width: 10%" id="th_defect_qty4" v-else class="table--row__align-top">
                            <div class="align-center">
                              B.CS.04<br />
                              CS4 Qty
                            </div>
                          </th>

                          <th style="width: 2%" id="th_defect_info"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(defect, idx) in allDefects" :key="defect" class="table--row__align-top">
                          <td style="width: 7%">
                            <v-checkbox
                              :id="'checkbox_selectDefect_' + idx"
                              v-model="defect.isSelected"
                              @click="updateDefectSelection(defect)"
                              hide-details
                              :disabled="!isEditable"
                            ></v-checkbox>
                          </td>
                          <td class="align-center" style="padding-top: 8px !important; width: 23%">
                            {{ defect.elemKey }} - {{ defect.elemShortName }}
                          </td>
                          <td
                            style="width: 10%"
                            class="align-center"
                            :style="
                              updateElementQtyGroup === ELEMENTS.PERCENTAGE && defect.isSelected && isEditable
                                ? ''
                                : 'padding-top:8px !important'
                            "
                          >
                            <NumberField
                              :id="'text_defectQty_' + idx"
                              v-model="defect.elemQuantity"
                              :maxlength="quantityCountMaxLength"
                              :textStyle="'width: 100%'"
                              :readonly="
                                !(updateElementQtyGroup === ELEMENTS.PERCENTAGE && defect.isSelected && isEditable)
                              "
                              readOnlyValidation
                              :allowCommas="false"
                              @change="
                                defect.elemPctState1 = '100.00';
                                defect.elemQtyState1 = defect.elemQuantity;
                                updateDefectCount(defect);
                              "
                              :rules="
                                isEditable && defect.isSelected
                                  ? [
                                      (value) => {
                                        return greaterThanZeroRuleExclusive(value);
                                      },
                                      requiredRule,
                                    ]
                                  : []
                              "
                            />
                          </td>
                          <td
                            class="align-center"
                            :style="defect.isSelected && isEditable ? '' : 'padding-top:8px !important'"
                          >
                            <NumberField
                              :id="'text_defectSF_' + idx"
                              v-model="defect.elemScaleFactor"
                              :readonly="!defect.isSelected || !isEditable"
                              :decimalPlaces="1"
                              :textStyle="'width: 100%'"
                              :maxlength="12"
                              :rules="
                                isEditable
                                  ? [
                                      (value) => {
                                        return lessThanValueRule(value, 9999999.99);
                                      },
                                    ]
                                  : []
                              "
                              @change="updateDefectCount(defect)"
                            />
                          </td>
                          <td style="padding-top: 8px !important" class="align-center">
                            {{ configStore.getElementDefinition(defect.elemParentKey)?.elemScaleUnit }}
                          </td>
                          <td
                            class="align-center"
                            :style="defect.isSelected && isEditable ? '' : 'padding-top:8px !important'"
                          >
                            <NumberField
                              :textStyle="'width: 100%'"
                              v-if="updateElementQtyGroup === ELEMENTS.QUANTITY"
                              :id="'text_defectQty1_' + idx"
                              v-model="defect.elemQtyState1"
                              readonly
                              readOnlyValidation
                              :rules="defect.isSelected && isEditable ? [requiredRule] : []"
                            />
                            <NumberField
                              v-else
                              :textStyle="'width: 100%'"
                              :id="'text_defectPct1_' + idx"
                              v-model="defect.elemPctState1"
                              readonly
                              :decimalPlaces="2"
                              readOnlyValidation
                              :rules="defect.isSelected && isEditable ? [requiredRule] : []"
                            />
                          </td>
                          <td
                            class="align-center"
                            :style="defect.isSelected && isEditable ? '' : 'padding-top:8px !important'"
                          >
                            <NumberField
                              :textStyle="'width: 100%'"
                              v-if="updateElementQtyGroup === ELEMENTS.QUANTITY"
                              :id="'text_defectQty2_' + idx"
                              v-model="defect.elemQtyState2"
                              :allowCommas="false"
                              :readonly="!defect.isSelected || !isEditable"
                              @change="updateDefectCount(defect)"
                              :rules="
                                isEditable && defect.isSelected
                                  ? [requiredRule, elementOneQuantityMustBeGreaterThanZero(defect)]
                                  : []
                              "
                            />
                            <NumberField
                              :textStyle="'width: 100%'"
                              v-else
                              :id="'text_defectPct2_' + idx"
                              v-model="defect.elemPctState2"
                              :readonly="!defect.isSelected || !isEditable"
                              :decimalPlaces="2"
                              @change="updateDefectCount(defect)"
                              :rules="
                                isEditable && defect.isSelected
                                  ? [
                                      (value) => {
                                        return lessThanValueRule(value, 100);
                                      },
                                      elementTotalPercentEqualToOneHundredRule(defect),
                                      requiredRule,
                                    ]
                                  : []
                              "
                            />
                          </td>
                          <td
                            class="align-center"
                            :style="defect.isSelected && isEditable ? '' : 'padding-top:8px !important'"
                          >
                            <NumberField
                              :textStyle="'width: 100%'"
                              v-if="updateElementQtyGroup === ELEMENTS.QUANTITY"
                              :id="'text_defectQty3_' + idx"
                              v-model="defect.elemQtyState3"
                              :allowCommas="false"
                              :readonly="!defect.isSelected || !isEditable"
                              @change="updateDefectCount(defect)"
                              :rules="
                                isEditable && defect.isSelected
                                  ? [requiredRule, elementOneQuantityMustBeGreaterThanZero(defect)]
                                  : []
                              "
                            />
                            <NumberField
                              :textStyle="'width: 100%'"
                              v-else
                              :id="'text_defectPct3_' + idx"
                              v-model="defect.elemPctState3"
                              :readonly="!defect.isSelected || !isEditable"
                              :decimalPlaces="2"
                              @change="updateDefectCount(defect)"
                              :rules="
                                isEditable && defect.isSelected
                                  ? [
                                      (value) => {
                                        return lessThanValueRule(value, 100);
                                      },
                                      elementTotalPercentEqualToOneHundredRule(defect),
                                      requiredRule,
                                    ]
                                  : []
                              "
                            />
                          </td>
                          <td
                            class="align-center"
                            :style="defect.isSelected && isEditable ? '' : 'padding-top:8px !important'"
                          >
                            <NumberField
                              :textStyle="'width: 100%'"
                              v-if="updateElementQtyGroup === ELEMENTS.QUANTITY"
                              :id="'text_defectQty4_' + idx"
                              v-model="defect.elemQtyState4"
                              :allowCommas="false"
                              :readonly="!defect.isSelected || !isEditable"
                              @change="updateDefectCount(defect)"
                              :rules="
                                isEditable && defect.isSelected
                                  ? [requiredRule, elementOneQuantityMustBeGreaterThanZero(defect)]
                                  : []
                              "
                            />
                            <NumberField
                              :textStyle="'width: 100%'"
                              v-else
                              :id="'text_defectPct4_' + idx"
                              v-model="defect.elemPctState4"
                              :readonly="!defect.isSelected || !isEditable"
                              :decimalPlaces="2"
                              @change="updateDefectCount(defect)"
                              :rules="
                                isEditable && defect.isSelected
                                  ? [
                                      (value) => {
                                        return lessThanValueRule(value, 100);
                                      },
                                      elementTotalPercentEqualToOneHundredRule(defect),
                                      requiredRule,
                                    ]
                                  : []
                              "
                            />
                          </td>
                          <td style="padding-top: 8px !important">
                            <v-icon
                              icon="fas fa-circle-info"
                              class="infoIcon"
                              @click="openElementConditionDialog(defect)"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </v-table>
                  </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel id="panel_protectiveSystems" value="protectiveSystems">
                  <v-expansion-panel-title
                    class="d-flex justify-start"
                    hide-actions
                    expand-icon="fa-solid fa-plus"
                    collapse-icon="fa-solid fa-minus"
                  >
                    <v-icon v-if="elementsPanel.includes('protectiveSystems')" icon="fa-solid fa-minus" />
                    <v-icon v-else icon="fa-solid fa-plus" />
                    <h3>Protective Systems</h3>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <v-table class="w-100 compactTable elementsTable">
                      <thead>
                        <tr>
                          <th style="width: 7%" id="th_proSys_applied"><br />Applied</th>
                          <th
                            style="width: 23%"
                            id="th_proSys_elem"
                            class="dialog-table-cell_width__desc table--row__align-top"
                          >
                            <div class="align-center">
                              B.E.01<br />
                              Element Description
                            </div>
                          </th>
                          <th style="width: 10%" id="th_proSys_qty" class="table--row__align-top">
                            <div class="align-center">
                              CS.01<br />
                              Qty
                            </div>
                          </th>
                          <th style="width: 10%" id="th_proSys_sf" class="align-center">CS.02<br />SF</th>
                          <th id="th_proSys_uom" style="width: 8%" class="align-center">
                            E.01<br />
                            UOM
                          </th>
                          <th
                            style="width: 10%"
                            id="th_proSys_pct1"
                            v-if="updateElementQtyGroup === ELEMENTS.PERCENTAGE"
                            class="table--row__align-top"
                          >
                            <div class="align-center">
                              B.CS.01<br />
                              CS1 Pct
                            </div>
                          </th>
                          <th style="width: 10%" id="th_proSys_qty1" v-else class="table--row__align-top">
                            <div class="align-center">
                              B.CS.01<br />
                              CS1 Qty
                            </div>
                          </th>
                          <th
                            style="width: 10%"
                            id="th_proSys_pct2"
                            v-if="updateElementQtyGroup === ELEMENTS.PERCENTAGE"
                            class="table--row__align-top"
                          >
                            <div class="align-center">
                              B.CS.02<br />
                              CS2 Pct
                            </div>
                          </th>
                          <th style="width: 10%" id="th_proSys_qty2" v-else class="table--row__align-top">
                            <div class="align-center">
                              B.CS.02<br />
                              CS2 Qty
                            </div>
                          </th>
                          <th
                            style="width: 10%"
                            id="th_proSys_pct3"
                            v-if="updateElementQtyGroup === ELEMENTS.PERCENTAGE"
                            class="table--row__align-top"
                          >
                            <div class="align-center">B.CS.03 <br />CS3 Pct</div>
                          </th>
                          <th style="width: 10%" id="th_proSys_qty3" v-else class="table--row__align-top">
                            <div class="align-center">
                              B.CS.03<br />
                              CS3 Qty
                            </div>
                          </th>
                          <th
                            style="width: 10%"
                            id="th_proSys_pct4"
                            v-if="updateElementQtyGroup === ELEMENTS.PERCENTAGE"
                            class="table--row__align-top"
                          >
                            <div class="align-center">
                              B.CS.04<br />
                              CS4 Pct
                            </div>
                          </th>
                          <th style="width: 10%" id="th_proSys_qty4" v-else class="table--row__align-top">
                            <div class="align-center">
                              B.CS.04<br />
                              CS4 Qty
                            </div>
                          </th>

                          <th style="width: 2%" id="th_proSys_info"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <template v-for="(protectiveSystem, idx) in allProtectiveSystems" :key="protectiveSystem">
                          <tr class="table--row__align-top">
                            <td>
                              <v-checkbox
                                :id="'checkbox_selectProtectiveSystem_' + idx"
                                v-model="protectiveSystem.isSelected"
                                @click="updateProtectiveSystemSelection(protectiveSystem)"
                                hide-details
                                :disabled="!isEditable"
                              ></v-checkbox>
                            </td>
                            <td style="padding-top: 8px !important" class="align-center">
                              {{ protectiveSystem.elemKey }} -
                              {{ protectiveSystem.elemShortName }}
                            </td>
                            <td
                              class="align-center"
                              :style="protectiveSystem.isSelected && isEditable ? '' : 'padding-top:8px !important'"
                            >
                              <NumberField
                                :textStyle="'width: 100%'"
                                :id="'text_systemQty_' + idx"
                                v-model="protectiveSystem.elemQuantity"
                                :maxlength="quantityCountMaxLength"
                                :readonly="!protectiveSystem.isSelected || !isEditable"
                                :allowCommas="false"
                                @change="
                                  protectiveSystem.elemQtyState1 = protectiveSystem.elemQuantity;
                                  protectiveSystem.elemPctState1 = '100.00';
                                  updateProtectiveSystemCount(protectiveSystem);
                                "
                                :rules="
                                  isEditable && protectiveSystem.isSelected
                                    ? [
                                        (value) => {
                                          return greaterThanZeroRuleExclusive(value);
                                        },
                                        requiredRule,
                                      ]
                                    : []
                                "
                              />
                            </td>
                            <td
                              class="align-center"
                              :style="protectiveSystem.isSelected && isEditable ? '' : 'padding-top:8px !important'"
                            >
                              <NumberField
                                :textStyle="'width: 100%'"
                                :id="'text_systemSF_' + idx"
                                v-model="protectiveSystem.elemScaleFactor"
                                :readonly="!protectiveSystem.isSelected || !isEditable"
                                :decimalPlaces="1"
                                @change="updateProtectiveSystemCount(protectiveSystem)"
                                :maxlength="12"
                                :rules="
                                  isEditable
                                    ? [
                                        (value) => {
                                          return lessThanValueRule(value, 9999999.99);
                                        },
                                      ]
                                    : []
                                "
                              />
                            </td>
                            <td style="padding-top: 8px !important" class="align-center">
                              {{ configStore.getElementDefinition(protectiveSystem.elemKey)?.elemScaleUnit }}
                            </td>
                            <td class="align-center">
                              <NumberField
                                :textStyle="'width: 100%'"
                                v-if="updateElementQtyGroup === ELEMENTS.QUANTITY"
                                :id="'text_systemQty1_' + idx"
                                v-model="protectiveSystem.elemQtyState1"
                                readonly
                                readOnlyValidation
                                :rules="isEditable && protectiveSystem.isSelected ? [requiredRule] : []"
                              />
                              <NumberField
                                v-else
                                :id="'textsystemPct1_' + idx"
                                v-model="protectiveSystem.elemPctState1"
                                readonly
                                readOnlyValidation
                                :rules="isEditable && protectiveSystem.isSelected ? [requiredRule] : []"
                                :decimalPlaces="2"
                              />
                            </td>
                            <td class="align-center">
                              <NumberField
                                :textStyle="'width: 100%'"
                                v-if="updateElementQtyGroup === ELEMENTS.QUANTITY"
                                :id="'text_systemQty2_' + idx"
                                v-model="protectiveSystem.elemQtyState2"
                                readonly
                                :rules="isEditable && protectiveSystem.isSelected ? [requiredRule] : []"
                                readOnlyValidation
                              />
                              <NumberField
                                :textStyle="'width: 100%'"
                                v-else
                                :id="'text_systemPct2_' + idx"
                                v-model="protectiveSystem.elemPctState2"
                                readonly
                                :decimalPlaces="2"
                                :rules="isEditable && protectiveSystem.isSelected ? [requiredRule] : []"
                                readOnlyValidation
                              />
                            </td>
                            <td class="align-center">
                              <NumberField
                                :textStyle="'width: 100%'"
                                v-if="updateElementQtyGroup === ELEMENTS.QUANTITY"
                                :id="'text_systemQty3_' + idx"
                                v-model="protectiveSystem.elemQtyState3"
                                readonly
                                readOnlyValidation
                                :rules="isEditable && protectiveSystem.isSelected ? [requiredRule] : []"
                              />
                              <NumberField
                                :textStyle="'width: 100%'"
                                v-else
                                :id="'text_systemPct3_' + idx"
                                v-model="protectiveSystem.elemPctState3"
                                readonly
                                :decimalPlaces="2"
                                readOnlyValidation
                                :rules="isEditable && protectiveSystem.isSelected ? [requiredRule] : []"
                              />
                            </td>
                            <td class="align-center">
                              <NumberField
                                :textStyle="'width: 100%'"
                                v-if="updateElementQtyGroup === ELEMENTS.QUANTITY"
                                :id="'text_systemQty4_' + idx"
                                v-model="protectiveSystem.elemQtyState4"
                                readonly
                                readOnlyValidation
                                :rules="isEditable && protectiveSystem.isSelected ? [requiredRule] : []"
                              />
                              <NumberField
                                :textStyle="'width: 100%'"
                                v-else
                                :id="'text_systemPct4_' + idx"
                                v-model="protectiveSystem.elemPctState4"
                                readonly
                                :decimalPlaces="2"
                                readOnlyValidation
                                :rules="isEditable && protectiveSystem.isSelected ? [requiredRule] : []"
                              />
                            </td>
                            <td></td>
                          </tr>
                          <template v-for="(subSystem, subIdx) in protectiveSystem?.SUB_COMPS" :key="subSystem">
                            <tr class="table--row__align-top">
                              <td>
                                <div style="float: left" class="pl-10 h-100 pt-2">
                                  <v-icon
                                    icon="fa-solid fa-bolt-lightning"
                                    :style="protectiveSystem.isSelected ? 'opacity: 1' : 'opacity: 0.5'"
                                  />
                                </div>
                                <v-checkbox
                                  :id="'checkbox_selectSubSystem_' + idx + '_' + subIdx"
                                  v-model="subSystem.isSelected"
                                  @click="updateProtectiveSystemSelection(subSystem)"
                                  :disabled="!protectiveSystem.isSelected || !isEditable"
                                  hide-details
                                ></v-checkbox>
                              </td>
                              <td style="padding-top: 8px !important" class="align-center">
                                {{ subSystem.elemKey }} -
                                {{ subSystem.elemShortName }}
                              </td>
                              <td class="align-center">
                                <NumberField
                                  :textStyle="'width: 100%'"
                                  :id="'text_subSystemQty_' + idx + '_' + subIdx"
                                  :readonly="
                                    !(
                                      updateElementQtyGroup === ELEMENTS.PERCENTAGE &&
                                      subSystem.isSelected &&
                                      isEditable
                                    )
                                  "
                                  :allowCommas="false"
                                  @change="
                                    subSystem.elemPctState1 = '100.00';
                                    subSystem.elemQtyState1 = subSystem.elemQuantity;
                                    updateProtectiveSystemCount(subSystem);
                                  "
                                  v-model="subSystem.elemQuantity"
                                  :rules="
                                    subSystem.isSelected && isEditable
                                      ? [
                                          (value) => {
                                            return greaterThanZeroRuleExclusive(value);
                                          },
                                          requiredRule,
                                        ]
                                      : []
                                  "
                                  readOnlyValidation
                                />
                              </td>
                              <td class="align-center">
                                <NumberField
                                  :textStyle="'width: 100%'"
                                  :id="'text_subSystemSF_' + idx + '_' + subIdx"
                                  :readonly="!subSystem.isSelected || !isEditable"
                                  v-model="subSystem.elemScaleFactor"
                                  :rules="
                                    isEditable
                                      ? [
                                          (value) => {
                                            return lessThanValueRule(value, 9999999.99);
                                          },
                                        ]
                                      : []
                                  "
                                  :decimalPlaces="1"
                                  @change="updateProtectiveSystemCount(subSystem)"
                                />
                              </td>
                              <td style="padding-top: 8px !important" class="align-center">
                                {{ configStore.getElementDefinition(subSystem.elemParentKey)?.elemScaleUnit }}
                              </td>
                              <td
                                class="align-center"
                                :style="subSystem.isSelected && isEditable ? '' : 'padding-top:8px !important'"
                              >
                                <NumberField
                                  :textStyle="'width: 100%'"
                                  v-if="updateElementQtyGroup === ELEMENTS.QUANTITY"
                                  :id="'text_subSystemQty1_' + idx + '_' + subIdx"
                                  v-model="subSystem.elemQtyState1"
                                  readonly
                                  readOnlyValidation
                                  :rules="subSystem.isSelected && isEditable ? [requiredRule] : []"
                                />
                                <NumberField
                                  :textStyle="'width: 100%'"
                                  v-else
                                  :id="'text_subSystemPct1_' + idx + '_' + subIdx"
                                  v-model="subSystem.elemPctState1"
                                  readonly
                                  :decimalPlaces="2"
                                  readOnlyValidation
                                  :rules="isEditable && subSystem.isSelected ? [requiredRule] : []"
                                />
                              </td>
                              <td
                                class="align-center"
                                :style="subSystem.isSelected && isEditable ? '' : 'padding-top:8px !important'"
                              >
                                <NumberField
                                  :textStyle="'width: 100%'"
                                  v-if="updateElementQtyGroup === ELEMENTS.QUANTITY"
                                  :id="'text_subSystemQty2_' + idx + '_' + subIdx"
                                  v-model="subSystem.elemQtyState2"
                                  :readonly="!subSystem.isSelected || !isEditable"
                                  :allowCommas="false"
                                  @change="updateProtectiveSystemCount(subSystem)"
                                  :rules="
                                    subSystem.isSelected && isEditable
                                      ? [requiredRule, elementOneQuantityMustBeGreaterThanZero(subSystem)]
                                      : []
                                  "
                                />
                                <NumberField
                                  :textStyle="'width: 100%'"
                                  v-else
                                  :id="'text_subSystemPct2_' + idx + '_' + subIdx"
                                  v-model="subSystem.elemPctState2"
                                  :readonly="!subSystem.isSelected || !isEditable"
                                  :decimalPlaces="2"
                                  @change="updateProtectiveSystemCount(subSystem)"
                                  :rules="
                                    isEditable && subSystem.isSelected
                                      ? [
                                          (value) => {
                                            return lessThanValueRule(value, 100);
                                          },
                                          elementTotalPercentEqualToOneHundredRule(subSystem),
                                          requiredRule,
                                        ]
                                      : []
                                  "
                                />
                              </td>
                              <td
                                class="align-center"
                                :style="subSystem.isSelected && isEditable ? '' : 'padding-top:8px !important'"
                              >
                                <NumberField
                                  :textStyle="'width: 100%'"
                                  v-if="updateElementQtyGroup === ELEMENTS.QUANTITY"
                                  :id="'text_subSystemQty3_' + idx + '_' + subIdx"
                                  v-model="subSystem.elemQtyState3"
                                  :readonly="!subSystem.isSelected || !isEditable"
                                  :allowCommas="false"
                                  @change="updateProtectiveSystemCount(subSystem)"
                                  :rules="
                                    subSystem.isSelected && isEditable
                                      ? [requiredRule, elementOneQuantityMustBeGreaterThanZero(subSystem)]
                                      : []
                                  "
                                />
                                <NumberField
                                  :textStyle="'width: 100%'"
                                  v-else
                                  :id="'text_subSystemPct3_' + idx + '_' + subIdx"
                                  v-model="subSystem.elemPctState3"
                                  :readonly="!subSystem.isSelected || !isEditable"
                                  :decimalPlaces="2"
                                  @change="updateProtectiveSystemCount(subSystem)"
                                  :rules="
                                    isEditable && subSystem.isSelected
                                      ? [
                                          (value) => {
                                            return lessThanValueRule(value, 100);
                                          },
                                          elementTotalPercentEqualToOneHundredRule(subSystem),
                                          requiredRule,
                                        ]
                                      : []
                                  "
                                />
                              </td>
                              <td
                                class="align-center"
                                :style="subSystem.isSelected && isEditable ? '' : 'padding-top:8px !important'"
                              >
                                <NumberField
                                  :textStyle="'width: 100%'"
                                  v-if="updateElementQtyGroup === ELEMENTS.QUANTITY"
                                  :id="'text_subSystemQty4_' + idx + '_' + subIdx"
                                  v-model="subSystem.elemQtyState4"
                                  :readonly="!subSystem.isSelected || !isEditable"
                                  :allowCommas="false"
                                  @change="updateProtectiveSystemCount(subSystem)"
                                  :rules="
                                    subSystem.isSelected && isEditable
                                      ? [requiredRule, elementOneQuantityMustBeGreaterThanZero(subSystem)]
                                      : []
                                  "
                                />
                                <NumberField
                                  :textStyle="'width: 100%'"
                                  v-else
                                  :id="'text_subSystemPct4_' + idx + '_' + subIdx"
                                  v-model="subSystem.elemPctState4"
                                  :readonly="!subSystem.isSelected || !isEditable"
                                  :decimalPlaces="2"
                                  @change="updateProtectiveSystemCount(subSystem)"
                                  :rules="
                                    isEditable && subSystem.isSelected
                                      ? [
                                          (value) => {
                                            return lessThanValueRule(value, 100);
                                          },
                                          elementTotalPercentEqualToOneHundredRule(subSystem),
                                          requiredRule,
                                        ]
                                      : []
                                  "
                                />
                              </td>
                              <td style="padding-top: 8px !important">
                                <v-icon
                                  icon="fas fa-circle-info"
                                  class="infoIcon"
                                  @click="openElementConditionDialog(subSystem)"
                                />
                              </td>
                            </tr>
                          </template>
                        </template>
                      </tbody>
                    </v-table>
                  </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel id="panel_subComponents" v-if="allSubComponents.length > 0" value="subComponents">
                  <v-expansion-panel-title
                    class="d-flex justify-start"
                    hide-actions
                    expand-icon="fa-solid fa-plus"
                    collapse-icon="fa-solid fa-minus"
                  >
                    <v-icon v-if="elementsPanel.includes('subComponents')" icon="fa-solid fa-minus" />
                    <v-icon v-else icon="fa-solid fa-plus" />
                    <h3>Sub Components</h3>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <v-table class="compactTable w-100 elementsTable">
                      <thead>
                        <tr>
                          <th id="th_subcomp_applied" style="width: 7%"><br />Applied</th>
                          <th
                            style="width: 23%"
                            id="th_subcomp_elem"
                            class="dialog-table-cell_width__desc table--row__align-top"
                          >
                            <div class="align-center">
                              B.E.01<br />
                              Element Description
                            </div>
                          </th>
                          <th style="width: 10%" id="th_subcomp_qty" class="table--row__align-top">
                            <div class="align-center">
                              CS.02<br />
                              Qty
                            </div>
                          </th>
                          <th style="width: 10%" id="th_subcomp_sf" class="align-center"><br />CS.02<br />SF</th>
                          <th style="width: 8%" id="th_subcomp_uom" class="align-center"><br />E.01<br />UOM</th>
                          <th
                            style="width: 10%"
                            id="th_subcomp_pct1"
                            v-if="updateElementQtyGroup === ELEMENTS.PERCENTAGE"
                            class="table--row__align-top"
                          >
                            <div class="align-center">B.CS.01</div>
                          </th>
                          <th style="width: 10%" id="th_subcomp_qty1" v-else class="table--row__align-top">
                            <div class="align-center">
                              B.CS.01<br />
                              CS1 Qty
                            </div>
                          </th>
                          <th
                            style="width: 10%"
                            id="th_subcomp_pct2"
                            v-if="updateElementQtyGroup === ELEMENTS.PERCENTAGE"
                            class="table--row__align-top"
                          >
                            <div class="align-center">B.CS.02 <br />CS2 Pct</div>
                          </th>
                          <th style="width: 10%" id="th_subcomp_qty2" v-else class="table--row__align-top">
                            <div class="align-center">
                              B.CS.02<br />
                              CS2 Qty
                            </div>
                          </th>
                          <th
                            style="width: 10%"
                            id="th_subcomp_pct3"
                            v-if="updateElementQtyGroup === ELEMENTS.PERCENTAGE"
                            class="table--row__align-top"
                          >
                            <div class="align-center">B.CS.03 <br />CS3 Pct</div>
                          </th>
                          <th style="width: 10%" id="th_subcomp_qty3" v-else class="table--row__align-top">
                            <div class="align-center">
                              B.CS.03<br />
                              CS3 Qty
                            </div>
                          </th>
                          <th
                            style="width: 10%"
                            id="th_subcomp_pct4"
                            v-if="updateElementQtyGroup === ELEMENTS.PERCENTAGE"
                            class="table--row__align-top"
                          >
                            <div class="align-center">
                              B.CS.04<br />
                              CS4 Pct
                            </div>
                          </th>
                          <th style="width: 10%" id="th_subcomp_qty4" v-else class="table--row__align-top">
                            <div class="align-center">
                              B.CS.04<br />
                              CS4 Qty
                            </div>
                          </th>

                          <th style="width: 2%" id="th_subcomp_info"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(subcomp, sidx) in allSubComponents" :key="subcomp" class="table--row__align-top">
                          <td>
                            <v-checkbox
                              :id="'checkbox_selectsubcomp_' + sidx"
                              v-model="subcomp.isSelected"
                              @click="updateDefectSelection(subcomp)"
                              hide-details
                              :disabled="!isEditable"
                            ></v-checkbox>
                          </td>
                          <td style="padding-top: 8px !important" class="align-center">
                            {{ subcomp.elemKey }} - {{ subcomp.elemShortName }}
                          </td>
                          <td
                            class="align-center"
                            :style="subcomp.isSelected && isEditable ? '' : 'padding-top:8px !important'"
                          >
                            <NumberField
                              :textStyle="'width: 100%'"
                              :id="'text_subcompQty_' + sidx"
                              v-model="subcomp.elemQuantity"
                              :maxlength="quantityCountMaxLength"
                              :readonly="!(subcomp.isSelected && isEditable)"
                              :allowCommas="false"
                              @change="
                                subcomp.elemPctState1 = '100.00';
                                subcomp.elemQtyState1 = subcomp.elemQuantity;
                                updateSubComponentCount(subcomp);
                              "
                              :rules="
                                subcomp.isSelected && isEditable
                                  ? [
                                      (value) => {
                                        return greaterThanZeroRuleExclusive(value);
                                      },
                                      requiredRule,
                                    ]
                                  : []
                              "
                            />
                          </td>
                          <td
                            class="align-center"
                            :style="subcomp.isSelected && isEditable ? '' : 'padding-top:8px !important'"
                          >
                            <NumberField
                              :textStyle="'width: 100%'"
                              :id="'text_subcompSF_' + sidx"
                              v-model="subcomp.elemScaleFactor"
                              :readonly="!subcomp.isSelected || !isEditable"
                              :decimalPlaces="1"
                              :maxlength="12"
                              :rules="
                                isEditable
                                  ? [
                                      (value) => {
                                        return lessThanValueRule(value, 9999999.99);
                                      },
                                    ]
                                  : []
                              "
                              @change="updateSubComponentCount(subcomp)"
                            />
                          </td>
                          <td style="padding-top: 8px !important" class="align-center">
                            {{ configStore.getElementDefinition(subcomp.elemKey)?.elemScaleUnit }}
                          </td>
                          <td class="align-center">
                            <NumberField
                              :textStyle="'width: 100%'"
                              v-if="updateElementQtyGroup === ELEMENTS.QUANTITY"
                              :id="'text_subcompQty1_' + sidx"
                              v-model="subcomp.elemQtyState1"
                              readonly
                              readOnlyValidation
                              :rules="subcomp.isSelected && isEditable ? [requiredRule] : []"
                            />
                            <NumberField
                              :textStyle="'width: 100%'"
                              v-else
                              :id="'text_subcompPct1_' + sidx"
                              v-model="subcomp.elemPctState1"
                              readonly
                              readOnlyValidation
                              :rules="isEditable && subcomp.isSelected ? [requiredRule] : []"
                              :decimalPlaces="2"
                            />
                          </td>
                          <td
                            class="align-center"
                            :style="subcomp.isSelected && isEditable ? '' : 'padding-top:8px !important'"
                          >
                            <NumberField
                              :textStyle="'width: 100%'"
                              v-if="updateElementQtyGroup === ELEMENTS.QUANTITY"
                              :id="'text_subcompQty2_' + sidx"
                              :allowCommas="false"
                              v-model="subcomp.elemQtyState2"
                              :readonly="!subcomp.isSelected || !isEditable"
                              @change="updateSubComponentCount(subcomp)"
                              :rules="
                                subcomp.isSelected && isEditable
                                  ? [requiredRule, elementOneQuantityMustBeGreaterThanZero(subcomp)]
                                  : []
                              "
                            />
                            <NumberField
                              :textStyle="'width: 100%'"
                              v-else
                              :id="'text_subcompPct2_' + sidx"
                              v-model="subcomp.elemPctState2"
                              :readonly="!subcomp.isSelected || !isEditable"
                              :decimalPlaces="2"
                              @change="updateSubComponentCount(subcomp)"
                              :rules="
                                isEditable && subcomp.isSelected
                                  ? [
                                      (value) => {
                                        return lessThanValueRule(value, 100);
                                      },
                                      elementTotalPercentEqualToOneHundredRule(subcomp),
                                      requiredRule,
                                    ]
                                  : []
                              "
                            />
                          </td>
                          <td
                            class="align-center"
                            :style="subcomp.isSelected && isEditable ? '' : 'padding-top:8px !important'"
                          >
                            <NumberField
                              :textStyle="'width: 100%'"
                              v-if="updateElementQtyGroup === ELEMENTS.QUANTITY"
                              :id="'text_subcompQty3_' + sidx"
                              v-model="subcomp.elemQtyState3"
                              :allowCommas="false"
                              :readonly="!subcomp.isSelected || !isEditable"
                              @change="updateSubComponentCount(subcomp)"
                              :rules="
                                subcomp.isSelected && isEditable
                                  ? [requiredRule, elementOneQuantityMustBeGreaterThanZero(subcomp)]
                                  : []
                              "
                            />
                            <NumberField
                              :textStyle="'width: 100%'"
                              v-else
                              :id="'text_subcompPct3_' + sidx"
                              v-model="subcomp.elemPctState3"
                              :readonly="!subcomp.isSelected || !isEditable"
                              :decimalPlaces="2"
                              @change="updateSubComponentCount(subcomp)"
                              :rules="
                                isEditable && subcomp.isSelected
                                  ? [
                                      (value) => {
                                        return lessThanValueRule(value, 100);
                                      },
                                      elementTotalPercentEqualToOneHundredRule(subcomp),
                                      requiredRule,
                                    ]
                                  : []
                              "
                            />
                          </td>
                          <td
                            class="align-center"
                            :style="subcomp.isSelected && isEditable ? '' : 'padding-top:8px !important'"
                          >
                            <NumberField
                              :textStyle="'width: 100%'"
                              v-if="updateElementQtyGroup === ELEMENTS.QUANTITY"
                              :id="'text_subcompQty4_' + sidx"
                              v-model="subcomp.elemQtyState4"
                              :allowCommas="false"
                              :readonly="!subcomp.isSelected || !isEditable"
                              @change="updateSubComponentCount(subcomp)"
                              :rules="
                                subcomp.isSelected && isEditable
                                  ? [requiredRule, elementOneQuantityMustBeGreaterThanZero(subcomp)]
                                  : []
                              "
                            />
                            <NumberField
                              :textStyle="'width: 100%'"
                              v-else
                              :id="'text_subcompPct4_' + sidx"
                              v-model="subcomp.elemPctState4"
                              :readonly="!subcomp.isSelected || !isEditable"
                              :decimalPlaces="2"
                              @change="updateSubComponentCount(subcomp)"
                              :rules="
                                isEditable && subcomp.isSelected
                                  ? [
                                      (value) => {
                                        return lessThanValueRule(value, 100);
                                      },
                                      elementTotalPercentEqualToOneHundredRule(subcomp),
                                      requiredRule,
                                    ]
                                  : []
                              "
                            />
                          </td>
                          <td></td>
                        </tr>
                      </tbody>
                    </v-table>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="justify-end">
        <v-btn
          variant="outlined"
          class="mx-5"
          @click="validateElementForm"
          :disabled="!isEditable || (isEditable && !valid) || !changesDetected"
          v-if="inspectionPage === INSPECTION_PAGES.ELEMENTS"
        >
          <span v-if="showElementDialog.isAdd">Add Element</span>
          <span v-else>Update Element</span>
        </v-btn>
        <v-btn @click="closeUpdateDialog" variant="outlined" class="mx-5"> Close </v-btn></v-card-actions
      >
    </v-card>
  </v-dialog>
  <!-- Element Definition lookup -->
  <v-dialog v-model="dialog" class="dialogForm">
    <v-card>
      <v-card-title>
        <span class="text-h5" style="font-weight: bold"
          >{{ selectedElementToShowInfo?.elementStateDefinitions?.[0]?.elemKey }}-{{
            selectedElementToShowInfo?.elementStateDefinitions?.[0]?.elemStateNameShort
          }}</span
        >
      </v-card-title>
      <v-divider />
      <v-card-text>
        <ol class="pl-3">
          <li v-for="condition in selectedElementToShowInfo?.elementStateDefinitions" :key="condition" class="pa-2">
            <span>{{ condition.elemStateDesc }}</span>
          </li>
        </ol>
        <v-divider />
        <span>{{ getElemConditionLegalText(selectedElementToShowInfo.legalTextCD) }}</span>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn
          variant="outlined"
          class="mx-5"
          @click="
            {
              dialog = false;
              resetSelections();
            }
          "
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <!-- Copy Element -->
  <v-dialog v-model="showCopyElementDialog" class="dialogForm" persistent>
    <v-card>
      <v-card-title>
        <span class="text-h5">Copy NBE Element</span>
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-form v-model="validCopyElementForm" ref="copyElementForm">
          <v-row
            ><v-col>
              <v-label class="bold">Source Structure Unit: </v-label>
              <v-select
                id="select_sourceStructureUnit"
                label=""
                :items="sourceStructureUnits"
                :modelValue="selectedCopyElementSourceStructureUnitId"
                @update:modelValue="
                  ($event) => {
                    selectedCopyElementSourceStructureUnitId = $event;
                    selectedCopyElementTargetStructureUnitId = null;
                    selectedCopyElementElementId = null;
                    getElements(selectedCopyElementSourceStructureUnitId);
                    setTargetUnits();
                    copyElementForm.validate();
                  }
                "
                hide-details="auto"
                persistent-placeholder
                required
                :rules="[requiredRule]"
              >
              </v-select
            ></v-col>
            <v-col>
              <v-label class="bold">Source Elem ID: </v-label>
              <v-select
                id="select_sourceElement"
                label=""
                :modelValue="selectedCopyElementElementId"
                :items="sourceElemsArr"
                @update:modelValue="
                  ($event) => {
                    selectedCopyElementElementId = $event;
                    selectedCopyElementTargetStructureUnitId = null;
                    setTargetUnits();
                    copyElementForm.validate();
                  }
                "
                hide-details="auto"
                persistent-placeholder
                :disabled="!selectedCopyElementSourceStructureUnitId"
                required
                :rules="[requiredRule]"
              >
              </v-select
            ></v-col>
            <v-col>
              <v-label class="bold">Target Structure Unit: </v-label>
              <v-select
                id="select_targetStructureUnit"
                label=""
                :modelValue="selectedCopyElementTargetStructureUnitId"
                :items="targetCopyElementStructureUnits"
                :hide-details="!selectedCopyElementElementId ? 'true' : 'auto'"
                @update:modelValue="
                  ($event) => {
                    targetCopyElementStructureUnits = $event;
                    copyElementForm.validate();
                  }
                "
                persistent-placeholder
                :disabled="!selectedCopyElementElementId"
                required
                :rules="[requiredRule]"
              >
              </v-select></v-col></v-row></v-form
      ></v-card-text>
      <v-card-actions class="justify-end">
        <v-btn
          variant="outlined"
          class="mx-5"
          @click="
            {
              copyElementSave();
              showCopyElementDialog = false;
            }
          "
          :disabled="!validCopyElementForm"
        >
          Save
        </v-btn>
        <v-btn
          variant="outlined"
          class="mx-5"
          @click="
            {
              showCopyElementDialog = false;
              resetSelections();
            }
          "
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <!-- Copy Structure Unit -->
  <v-dialog v-model="showCopyStructureUnitDialog" class="dialogForm" persistent>
    <v-card>
      <v-card-title>
        <span class="text-h5">Copy All Elements from a Structure Unit</span>
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-form v-model="validCopyStructureUnitForm">
          <v-row
            ><v-col>
              <v-label class="bold">Source Structure Unit: </v-label>
              <v-select
                id="select_sourceStructureUnit"
                :items="sourceStructureUnits"
                v-model="selectedCopyStructureUnitSourceId"
                label=""
                hide-details="auto"
                persistent-placeholder
                required
                :rules="[requiredRule]"
              >
              </v-select
            ></v-col>
            <v-col>
              <v-label class="bold">Target Structure Unit: </v-label>
              <v-select
                id="select_targetStructureUnit"
                label=""
                :items="targetCopyStructureUnits"
                v-model="selectedCopyStructureUnitTargetId"
                hide-details="auto"
                persistent-placeholder
                :disabled="!selectedCopyStructureUnitSourceId"
                required
                :rules="[requiredRule]"
              >
              </v-select></v-col></v-row
          ><v-row
            ><v-col><span>Cannot copy to Structure Unit that already has elements defined</span></v-col></v-row
          >
        </v-form>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn
          variant="outlined"
          class="mx-5"
          @click="
            {
              copyStructureUnitSave();
              showCopyStructureUnitDialog = false;
            }
          "
          :disabled="!validCopyStructureUnitForm"
        >
          Save
        </v-btn>
        <v-btn variant="outlined" class="mx-5" @click="showCopyStructureUnitDialog = false"> Close </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <ConfirmDialog ref="closeConfirm" :cancelButtonText="BUTTONS.NO" />
  <v-dialog v-model="systemElementMessage.show">
    <v-card class="pa-3 error-dialog">
      <p class="h3 mb-6"><strong>&nbsp;</strong></p>
      <p class="mb-2">{{ systemElementMessage.message }}</p>
      <button
        @click="systemElementMessage.show = false"
        class="d-flex align-end flex-column align-content-center close-button"
      >
        <v-icon size="x-large" icon="fas fa-xmark mx-2" />
        <small>CLOSE</small>
      </button>
    </v-card>
  </v-dialog>
</template>
<script>
import { ref, computed, watch, toRefs, nextTick, onMounted } from "vue";
import { useInspectionStore } from "@/stores/inspection";
import { clone } from "@/util/clone";
import { useConfigStore } from "@/stores/config";
import { ELEMENTS, BUTTONS, ERROR_MESSAGES } from "@/constants/CommonWebConstants";
import { getStructureType } from "@/composables/structureType";
import { ENV_CONFIG_PROPERTY } from "@/constants/EnvConfigProperties";
import {
  requiredRule,
  greaterThanZeroRule,
  lessThanValueRule,
  greaterThanZeroRuleExclusive,
  elementTotalPercentEqualToOneHundredRule,
  elementOneQuantityMustBeGreaterThanZero,
} from "@/composables/validationRules";
import {
  convertToInt,
  convertToFloat,
  convertToPercent,
  convertFromPercent,
  convertToDecimalString,
} from "@/composables/util";
import NumberField from "@/components/common/NumberField.vue";
import { LOGGER } from "@/util/logger";
import ConfirmDialog from "@/components/common/ConfirmDialog.vue";
import { STRUCTURE_TYPE, FUNCTIONAL_SYSTEM, INSPECTION_MESSAGES, MODULE_TYPES } from "@/constants/InspectionConstants";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import InputMessage from "@/components/common/InputMessage.vue";
import InspectionElementRow from "@/components/inspection/InspectionElementRow.vue";
import LabelText from "@/components/common/LabelText.vue";
import { INSPECTION_PAGES } from "@/constants/Inspections";
import LabelSelect from "@/components/common/LabelSelect.vue";
import ExpandCollapseToggle from "@/components/shared/ExpandCollapseToggle.vue";
import { useSnackbarStore } from "@/stores/snackbar";
import { SNACKBAR_MESSAGE_TYPES } from "@/constants/GlobalSnackbar.js";
import { buildElements, sortElements } from "@/composables/elementsHierarchy";
import { useElementsStore } from "@/components/inspection/store/elements";
import { useInspectionLinksStore } from "@/stores/inspectionLinks";
import { useQuickSearchStore } from "@/stores/quickSearch";
import { storeToRefs } from "pinia";
import { SNACKBAR_MARKER } from "@/constants/GlobalSnackbar";
import { useUserPermissionStore } from "@/stores/userPermission";
import { INSPECTION_LINKS_PERMISSIONS } from "@/rbac";
import { ACCESS_LEVELS } from "@/rbac/accessLevels.js";
import ValidationCountsHeader from "@/components/shared/ValidationCountsHeader.vue";

export default {
  name: "InspectionElements",
  props: {
    inspectionPage: { default: null },
    moduleType: { default: MODULE_TYPES.INSPECTION },
  },
  setup(props) {
    const inspectionStore = useInspectionStore();
    const configStore = useConfigStore();
    const snackbarStore = useSnackbarStore();
    let allDefects = ref([]);
    let allProtectiveSystems = ref([]);
    let allSubComponents = ref([]);
    let elementsValue = ref({});
    let structureUnitGroups = ref({});
    let dialog = ref(false);
    let selectedElementToShowInfo = ref({});
    let tempSelectedElemInspections = [];
    const modifyElementForm = ref(null);
    let valid = ref(true);
    let changesDetected = ref(false);
    const closeConfirm = ref(null);
    let validCopyElementForm = ref(false);
    let copyElementForm = ref(null);
    let validCopyStructureUnitForm = ref(false);
    const quantityCountMaxLength = ref(7);
    const { inspectionPage } = toRefs(props);
    let snackBarId = ref("");
    const quickSearchStore = useQuickSearchStore();
    const inspectionLinksStore = useInspectionLinksStore();
    const elementsStore = useElementsStore();
    const userPermissionStore = useUserPermissionStore();
    const quickSearchHeader = computed(() => quickSearchStore.getStructure());
    const { hasQuickSearchSucceeded } = storeToRefs(quickSearchStore);
    let resetDirty = ref(false);
    const structureUnitsToAddElems = ref([]);
    const structureUnitTypes = ref([]);
    //hadle expand/collapse of structure unit expansion panel
    let panel = ref([]);
    const allPanels = [];
    const initialized = ref(false);
    const defectsAndProtectivesOriginalState = ref({});

    //hadle  expand/collapse of elements under structure unit
    let elementsOpened = ref([]);
    //hadle  expand/collapse of sub components under protective system
    let protectiveSystemselementsOpened = ref([]);
    let functionalElementsOpened = ref([]);

    let showElementDialog = ref({});
    let showCopyElementDialog = ref(false);
    let showCopyStructureUnitDialog = ref(false);
    let selectedElement = ref({});
    let elementsPanel = ref(["defects", "protectiveSystems", "subComponents"]);

    const hasAccessToEdit = computed({
      get() {
        return userPermissionStore.checkUserPermission(
          INSPECTION_LINKS_PERMISSIONS[ACCESS_LEVELS.EDIT],
          userPermissionStore.getUserPermissionByBrkeyFromCache(elementsValue.value?.structureBridge?.brkey),
        );
      },
    });

    const isEditable = computed(() => {
      if (inspectionPage.value === INSPECTION_PAGES.TUNNELS) {
        return false;
      } else if (props.moduleType === MODULE_TYPES.MANAGEMENT) {
        return hasQuickSearchSucceeded.value && hasAccessToEdit.value;
      } else {
        return inspectionStore.isEditable;
      }
    });
    const isManagement = computed(() => props.moduleType == MODULE_TYPES.MANAGEMENT);

    const calculate = () => {
      structureUnitsToAddElems.value = elementsValue.value?.structureUnits?.filter((a) => {
        if (getStructureType(elementsValue.value.structureBridge?.servTypOn) === STRUCTURE_TYPE.TUNNEL) {
          return (
            a.legacyStructureType == "N" ||
            a.legacyStructureType == "R" ||
            a.legacyStructureType == "S" ||
            a.legacyStructureType == "Z"
          );
        } else if (getStructureType(elementsValue.value.structureBridge?.servTypOn) === STRUCTURE_TYPE.BRIDGE) {
          return a.legacyStructureType == "A" || a.legacyStructureType == "M" || a.legacyStructureType == "C";
        }
      });

      structureUnitTypes.value = structureUnitsToAddElems.value?.map((b) => ({
        title: `${b.strUnitKey} - Type - ${configStore.getReferenceValue(
          REFERENCE_TABLE.STRUCTURE_UNIT_TYPE,
          b.legacyStructureType,
        )}`,
        value: b.strUnitKey,
      }));
    };

    function getFunctionalSystem(elementKey) {
      //get the functional system for an element key(if exists)
      //get elemCat and elemType keys for elementKey
      const elemDef = configStore.getElementDefinition(elementKey);
      //get matching functional system
      return configStore
        .getAddElementDefinitions(getStructureType(elementsValue.value.structureBridge?.servTypOn))
        ?.find(
          (elem) =>
            elem.elemCatKey === elemDef?.elemCatKey &&
            elem.elemTypeKey === elemDef?.elemTypeKey &&
            elem.elemMatKey != elemDef?.elemMatKey &&
            elem.elemMatKey === FUNCTIONAL_SYSTEM.MAT_KEY,
        );
    }

    function setStructureUnitGroups() {
      for (const a of structureUnitsToAddElems.value ?? []) {
        structureUnitGroups.value[a.strUnitKey] = clone(a);
      }
    }

    function setFunctionalSystemElements(elems) {
      let functionalSystemElements = {};
      for (const item of elems ?? []) {
        if (item && item != null) {
          const list = structureUnitGroups.value[item?.strUnitKey]?.tempElements;
          if (list) {
            list.push(item);
          } else if (structureUnitGroups.value[item?.strUnitKey]) {
            structureUnitGroups.value[item?.strUnitKey].tempElements = [item];
          }
        }
        const fnSystem = getFunctionalSystem(item.elemKey);
        if (!fnSystem) {
          //handled below
        } else if (functionalSystemElements[fnSystem.elemKey]) {
          functionalSystemElements[fnSystem.elemKey].push(item);
        } else {
          functionalSystemElements[fnSystem.elemKey] = [item];
        }
      }
      return functionalSystemElements;
    }

    // Function to convert the json array of elements and its defects/protective systems to hierarchy so it can be displayed on UI
    function convertToHierarchy() {
      calculate();
      structureUnitGroups.value = {};
      panel.value = [];
      const filteredElements = elementsValue.value?.inspectionElementPons?.filter((item) => !item.deleted) || [];
      let elems = clone(filteredElements)?.sort((a, b) => a.strUnitKey - b.strUnitKey || a.elemKey - b.elemKey);
      //Add all structure units that can have elements to the hashtable
      setStructureUnitGroups();
      //structureUnitGroups is a hashtable with StructureUnit as key and list of elements as value
      let functionalSystemElements = setFunctionalSystemElements(elems);
      for (let key in structureUnitGroups.value) {
        let structureUnitElements = structureUnitGroups.value[key];
        //build elements hierarchy for each structure unit
        structureUnitElements.elements = buildElements(
          structureUnitElements?.tempElements,
          functionalSystemElements,
          elementsValue.value.structureBridge?.servTypOn,
        ).sort((a, b) => a.elemKey - b.elemKey);
        sortElements(structureUnitElements.elements, functionalSystemElements);
      }

      if (structureUnitGroups.value) {
        for (let index = 0; index < Object.keys(structureUnitGroups.value).length; index++) {
          panel.value.push("structureUnit_" + index);
          allPanels.push("structureUnit_" + index);
        }
      }

      if (!Object.keys(structureUnitGroups.value).length) {
        getDefaultStructureGroup();
      }
    }

    const elemDescriptionMaxLength = computed(() => {
      return configStore.getEnvConfigValue(ENV_CONFIG_PROPERTY.ELEM_DESC_MAX_LENGTH);
    });

    const elemConditionMaxLength = computed(() => {
      return configStore.getEnvConfigValue(ENV_CONFIG_PROPERTY.ELEM_CONDITION_MAX_LENGTH);
    });

    function getElemConditionLegalText(legalTextCode) {
      if (legalTextCode == 1) {
        return configStore.getEnvConfigValue(ENV_CONFIG_PROPERTY.ELEM_CONDITION_LEGAL_TEXT1);
      } else if (legalTextCode == 2) {
        return configStore.getEnvConfigValue(ENV_CONFIG_PROPERTY.ELEM_CONDITION_LEGAL_TEXT2);
      } else {
        return legalTextCode;
      }
    }

    let elementQtyGroup = ref(ELEMENTS.QUANTITY);
    let updateElementQtyGroup = ref(ELEMENTS.QUANTITY);

    function resetAddElementForUnitKeyChange(structureUnitKey) {
      updateElementQtyGroup.value = ELEMENTS.QUANTITY;
      elementsPanel.value = [];
      allDefects.value = [];
      allProtectiveSystems.value = [];
      allSubComponents.value = [];
      selectedElement.value.elemKey = null;
      selectedElement.value.strUnitKey = structureUnitKey;
      selectedElement.value.elemTag = "";
      selectedElement.value.envKey = 3;
      selectedElement.value.uom = "";
      selectedElement.value.elemQuantity = 0;
      selectedElement.value.elemScaleFactor = "1.0";
      selectedElement.value.elemDescription = "";
      selectedElement.value.elemNotes = "";
      selectedElement.value.elemQtyState1 = 0;
      selectedElement.value.elemQtyState2 = 0;
      selectedElement.value.elemQtyState3 = 0;
      selectedElement.value.elemQtyState4 = 0;
      selectedElement.value.elemPctState1 = 0;
      selectedElement.value.elemPctState2 = 0;
      selectedElement.value.elemPctState3 = 0;
      selectedElement.value.elemPctState4 = 0;
      selectedElement.value.elemParentKey = 0;
      selectedElement.value.elemGrandparentKey = 0;
      selectedElement.value.defects = [];
      selectedElement.value.protectiveSystems = [];

      nextTick(() => {
        modifyElementForm.value.validate();
        changesDetected.value = true;
      });
    }
    function addElement() {
      changesDetected.value = false;
      //clone the non null inspection elements - inspectionElementPons contains null object when there are no elements
      tempSelectedElemInspections = clone(
        elementsValue.value.inspectionElementPons ? elementsValue.value.inspectionElementPons?.filter((n) => n) : [],
      );

      showElementDialog.value.show = true;
      showElementDialog.value.isAdd = true;
      updateElementQtyGroup.value = ELEMENTS.QUANTITY;
      elementsPanel.value = [];
      allDefects.value = [];
      allProtectiveSystems.value = [];
      allSubComponents.value = [];
      selectedElement.value = {
        elemKey: null,
        strUnitKey: null,
        elemTag: "",
        envKey: 3,
        uom: "",
        elemQuantity: 0,
        elemScaleFactor: "1.0",
        elemDescription: "",
        elemNotes: "",
        elemQtyState1: 0,
        elemQtyState2: 0,
        elemQtyState3: 0,
        elemQtyState4: 0,
        elemPctState1: 0,
        elemPctState2: 0,
        elemPctState3: 0,
        elemPctState4: 0,
        elemParentKey: 0,
        elemGrandparentKey: 0,
        defects: [],
        protectiveSystems: [],
        elemInvId: getNextInvId(tempSelectedElemInspections),
      };
      tempSelectedElemInspections.push(getInspectionElementObject(selectedElement.value));
      nextTick(() => {
        modifyElementForm.value.validate();
      });
    }
    //Copy Element or Copy StructureUnit
    let selectedCopyElementSourceStructureUnitId = ref(null);
    let selectedCopyElementElementId = ref(null);
    let selectedCopyElementTargetStructureUnitId = ref(null);
    let selectedCopyStructureUnitSourceId = ref(null);
    let selectedCopyStructureUnitTargetId = ref(null);
    let sourceStructureUnitsArr = ref([]);
    let sourceStructureUnits = ref([]);
    let targetCopyStructureUnitsArr = ref([]);
    let targetCopyStructureUnits = ref([]);
    let targetCopyElementStructureUnitsArr = ref([]);
    let targetCopyElementStructureUnits = ref([]);

    function setCopySourceStructureUnits() {
      for (let key in structureUnitGroups.value) {
        //Functional systems can't be copied
        if (
          elementsValue.value?.structureUnits?.find((a) => a?.strUnitKey == key)?.legacyStructureType !=
          FUNCTIONAL_SYSTEM.STR_UNIT_TYPE
        ) {
          let structureUnit = structureUnitGroups.value?.[key];
          if (structureUnit?.elements?.length > 0) {
            sourceStructureUnitsArr.value.push(structureUnit);
          } else {
            targetCopyStructureUnitsArr.value.push(structureUnit);
          }
        }
      }
      sourceStructureUnits.value = sourceStructureUnitsArr.value?.map((b) => ({
        title: `${b?.strUnitKey} - Type -  ${configStore.getReferenceValue(
          REFERENCE_TABLE.STRUCTURE_UNIT_TYPE,
          b?.legacyStructureType,
        )}`,
        value: b?.strUnitKey,
      }));
      targetCopyStructureUnits.value = targetCopyStructureUnitsArr.value?.map((b) => ({
        title: `${b?.strUnitKey} - Type - ${configStore.getReferenceValue(
          REFERENCE_TABLE.STRUCTURE_UNIT_TYPE,
          b?.legacyStructureType,
        )}`,
        value: b?.strUnitKey,
      }));
    }

    //get elements of a structure unit
    let sourceElemsArr = ref([]);
    function getElements(structureUnitKey) {
      sourceElemsArr.value = structureUnitGroups.value[structureUnitKey]?.elements
        .map((b) => ({
          title: `${b.elemInvId}/${b.envKey}/${b.elemKey}-${configStore.getElementDefinition(b.elemKey)?.elemLongName}`,
          value: b.elemKey,
        }))
        .sort((a, b) => a.value - b.value);
    }

    function setTargetUnits() {
      targetCopyElementStructureUnitsArr.value = [];
      targetCopyElementStructureUnits.value = [];
      for (let key in structureUnitGroups.value) {
        let structureUnit = structureUnitGroups.value[key];
        if (
          elementsValue.value?.structureUnits?.find((a) => a.strUnitKey == key)?.legacyStructureType !=
            FUNCTIONAL_SYSTEM.STR_UNIT_TYPE &&
          !structureUnit?.elements.find((a) => a.elemKey == selectedCopyElementElementId.value)
        ) {
          targetCopyElementStructureUnitsArr.value.push(structureUnit);
        }
      }
      targetCopyElementStructureUnits.value = targetCopyElementStructureUnitsArr.value.map((b) => ({
        title: `${b.strUnitKey} - Type - ${configStore.getReferenceValue(
          REFERENCE_TABLE.STRUCTURE_UNIT_TYPE,
          b.legacyStructureType,
        )}`,
        value: b.strUnitKey,
      }));
    }

    function copyElement() {
      resetSelections();
      setCopySourceStructureUnits();
      showCopyElementDialog.value = true;
    }
    function copyStructureUnit() {
      resetSelections();
      setCopySourceStructureUnits();
      showCopyStructureUnitDialog.value = true;
    }
    function resetSelections() {
      selectedCopyElementSourceStructureUnitId.value = null;
      selectedCopyElementElementId.value = null;
      selectedCopyElementTargetStructureUnitId.value = null;
      selectedCopyStructureUnitSourceId.value = null;
      selectedCopyStructureUnitTargetId.value = null;
      sourceStructureUnitsArr.value = [];
      sourceStructureUnits.value = [];
      targetCopyStructureUnitsArr.value = [];
      targetCopyStructureUnits.value = [];
      targetCopyElementStructureUnitsArr.value = [];
      targetCopyElementStructureUnits.value = [];
      sourceElemsArr.value = [];
    }
    let noFunctionalSystemMessage = ref(null);
    function getDefectsAndProtectiveSystems(elemKey) {
      selectedElement.value.uom = configStore.getElementDefinition(elemKey)?.elemScaleUnit;
      noFunctionalSystemMessage.value = null;
      //check if selected element should have a functional system before added
      const fnSystem = getFunctionalSystem(elemKey);

      if (!fnSystem) {
        getAllDefects(elemKey);
        getAllProtectiveSystems(elemKey);
        getAllSubComponents(elemKey);
      } else if (tempSelectedElemInspections.some((addedElem) => addedElem.elemKey === fnSystem.elemKey)) {
        //if functional system is needed, check if it is already added
        noFunctionalSystemMessage.value = null;
        getAllDefects(elemKey);
        getAllProtectiveSystems(elemKey);
        getAllSubComponents(elemKey);
      } else {
        noFunctionalSystemMessage.value =
          INSPECTION_MESSAGES.SYSTEM_ELEMENT_REQUIRED + ` - ${fnSystem.elemKey}-${fnSystem.elemShortName}`;
      }
    }

    function getAllDefects(elemKey) {
      let elemWithChildren = configStore.getElementChildren(elemKey);
      allDefects.value = clone(elemWithChildren.defects);
    }
    function getAllProtectiveSystems(elemKey) {
      let elemWithChildren = configStore.getElementChildren(elemKey);
      allProtectiveSystems.value = clone(elemWithChildren.protectiveSystems);
    }
    function getAllSubComponents(elemKey) {
      let elemWithChildren = configStore.getElementChildren(elemKey);
      allSubComponents.value = clone(elemWithChildren.subComponents);
    }

    function toggle(id) {
      const index = elementsOpened.value.indexOf(id);
      if (index > -1) {
        elementsOpened.value.splice(index, 1);
        //close underlying protective system sub components.
        protectiveSystemselementsOpened.value = protectiveSystemselementsOpened.value.filter((a) => !a.includes(id));
        //close underlying functional system system sub components.
        functionalElementsOpened.value = functionalElementsOpened.value.filter((a) => !a.includes(id));
      } else {
        elementsOpened.value.push(id);
      }
    }
    function toggleProtectiveSystems(id) {
      const index = protectiveSystemselementsOpened.value.indexOf(id);
      if (index > -1) {
        protectiveSystemselementsOpened.value.splice(index, 1);
        //close underlying functional system system sub components.
        functionalElementsOpened.value = functionalElementsOpened.value.filter((a) => a.includes(id));
      } else {
        protectiveSystemselementsOpened.value.push(id);
      }
    }
    function toggleFunctionalElements(id) {
      const index = functionalElementsOpened.value.indexOf(id);
      if (index > -1) {
        functionalElementsOpened.value.splice(index, 1);
      } else {
        functionalElementsOpened.value.push(id);
      }
    }

    const quantityRules = [
      (qty) => {
        if (+qty < 1) {
          return "Must be greater than 0";
        }
        return true;
      },
    ];

    function setCounts(selected, element) {
      selected.isSelected = true;
      selected.elemInvId = element.elemInvId;
      selected.elemQuantity = element.elemQuantity;
      selected.elemQtyState1 = element.elemQtyState1;
      selected.elemQtyState2 = element.elemQtyState2;
      selected.elemQtyState3 = element.elemQtyState3;
      selected.elemQtyState4 = element.elemQtyState4;
      selected.elemPctState1 = convertToDecimalString(element.elemPctState1, 2);
      selected.elemPctState2 = convertToDecimalString(element.elemPctState2, 2);
      selected.elemPctState3 = convertToDecimalString(element.elemPctState3, 2);
      selected.elemPctState4 = convertToDecimalString(element.elemPctState4, 2);
      selected.elemScaleFactor = element.elemScaleFactor;
    }

    function editUnit(element) {
      changesDetected.value = false;
      updateElementQtyGroup.value = ELEMENTS.QUANTITY;
      tempSelectedElemInspections = clone(elementsValue.value.inspectionElementPons);
      showElementDialog.value.isAdd = false;
      elementsPanel.value = ["defects", "protectiveSystems", "subComponents"];
      let { children, ...props } = element;
      selectedElement.value = { ...props };
      selectedElement.value.elemScaleFactor = Number.parseFloat(selectedElement.value.elemScaleFactor).toFixed(1);
      selectedElement.value.defects = [];
      selectedElement.value.protectiveSystems = [];
      selectedElement.value.subComponents = [];

      for (const e of children ?? []) {
        //set scale factor to decimal
        e.elemScaleFactor = Number.parseFloat(e.elemScaleFactor).toFixed(1);
        if (configStore.getElementDefinition(e.elemKey).elemProtectSys === "Y") {
          selectedElement.value.protectiveSystems.push(e);
          for (const subSystem of e.children ?? []) {
            selectedElement.value.protectiveSystems.push(subSystem);
          }
        } else if (configStore.getElementDefinition(e.elemKey).elemSubComp === "Y") {
          selectedElement.value.subComponents.push(e);
        } else if (configStore.getElementDefinition(e.elemKey).elemSmartFlag === "Y") {
          selectedElement.value.defects.push(e);
        }
      }

      getDefectsAndProtectiveSystems(element.elemKey);
      //loop through selected defects and set selected flag
      for (const defect of selectedElement.value.defects ?? []) {
        let selectedDefect = allDefects.value.find((a) => a.elemKey == defect.elemKey);
        setCounts(selectedDefect, defect);
      }
      for (const subComp of selectedElement.value.subComponents ?? []) {
        let selectedSubComp = allSubComponents.value.find((a) => a.elemKey == subComp.elemKey);
        setCounts(selectedSubComp, subComp);
      }

      //loop through selected protective systems and set selected flag
      for (const system of selectedElement.value.protectiveSystems ?? []) {
        //sub components under protective system
        if (system.elemGrandparentKey) {
          let selectedSubcomponent = allProtectiveSystems.value
            .find((a) => a.elemKey == system.elemParentKey)
            .SUB_COMPS.find((c) => c.elemKey == system.elemKey);
          selectedSubcomponent.isSelected = true;
          selectedSubcomponent.elemInvId = system.elemInvId;
          selectedSubcomponent.elemQuantity = system.elemQuantity;
          selectedSubcomponent.elemQtyState1 = system.elemQtyState1;
          selectedSubcomponent.elemQtyState2 = system.elemQtyState2;
          selectedSubcomponent.elemQtyState3 = system.elemQtyState3;
          selectedSubcomponent.elemQtyState4 = system.elemQtyState4;
          selectedSubcomponent.elemPctState1 = convertToDecimalString(system.elemPctState1, 2);
          selectedSubcomponent.elemPctState2 = convertToDecimalString(system.elemPctState2, 2);
          selectedSubcomponent.elemPctState3 = convertToDecimalString(system.elemPctState3, 2);
          selectedSubcomponent.elemPctState4 = convertToDecimalString(system.elemPctState4, 2);
          selectedSubcomponent.elemScaleFactor = system.elemScaleFactor;
          selectedSubcomponent.elemParentId = system.elemParentId;
        } else if (system.elemParentKey) {
          let selectedProtectiveSystem = allProtectiveSystems.value.find((a) => a.elemKey == system.elemKey);
          selectedProtectiveSystem.isSelected = true;
          selectedProtectiveSystem.elemInvId = system.elemInvId;
          selectedProtectiveSystem.elemQuantity = system.elemQuantity
          selectedProtectiveSystem.elemQtyState1 = system.elemQtyState1
          selectedProtectiveSystem.elemQtyState2 = system.elemQtyState2
          selectedProtectiveSystem.elemQtyState3 = system.elemQtyState3
          selectedProtectiveSystem.elemQtyState4 = system.elemQtyState4
          selectedProtectiveSystem.elemPctState1 = convertToFloat(system.elemPctState1, 0);
          selectedProtectiveSystem.elemPctState2 = convertToFloat(system.elemPctState2, 0);
          selectedProtectiveSystem.elemPctState3 = convertToFloat(system.elemPctState3, 0);
          selectedProtectiveSystem.elemPctState4 = convertToFloat(system.elemPctState4, 0);
          selectedProtectiveSystem.elemScaleFactor = system.elemScaleFactor;
          selectedProtectiveSystem.elemParentId = system.elemParentId;
        }
      }
      nextTick(() => {
        modifyElementForm.value?.validate();
      });
    }
    let systemElementMessage = ref({ show: false, message: null });

    function deleteUnit(element, structureUnitKey) {
      systemElementMessage.value.show = false;
      const fnSystem =
        configStore.getElementDefinition(element.elemKey)?.elemMatKey === FUNCTIONAL_SYSTEM.MAT_KEY
          ? configStore.getElementDefinition(element.elemKey)
          : null;
      let fnElement;
      if (fnSystem) {
        //check if system has functional elements
        fnElement = element.children?.find((a) => {
          const def = configStore.getElementDefinition(a.elemKey);
          if (def.elemProtectSys === "Y" || def.elemSubComp === "Y" || def.elemSmartFlag === "Y") {
            systemElementMessage.value.message = null;
            systemElementMessage.value.show = false;
            return null;
          } else {
            //functional element exists
            systemElementMessage.value.show = true;
            systemElementMessage.value.message = INSPECTION_MESSAGES.SYSTEM_DELETE_ERROR;
            return a;
          }
        });
      }
      if (!fnElement) {
        let tobeDeletedIds = [];
        //get the elements for which the desired element key(ex: 10601) is elemGrandparentKey
        const grandChildren = elementsValue.value.inspectionElementPons.filter(
          (e) => e.strUnitKey == structureUnitKey && e.elemGrandparentKey == element.elemKey,
        );
        //check if the grandchildren parents are actually children of the desired element
        for (const g of grandChildren ?? []) {
          const parentId = elementsValue.value.inspectionElementPons.find(
            (e) =>
              e.strUnitKey == structureUnitKey && g.elemParentId == e.elemInvId && e.elemParentId == element.elemInvId,
          )?.elemInvId;
          if (parentId) {
            tobeDeletedIds.push(parentId, g.elemInvId);
          }
        }
        tobeDeletedIds.push(element.elemInvId);
        const childrenIds = elementsValue.value.inspectionElementPons
          .filter((e) => e.strUnitKey == structureUnitKey && e.elemParentId == element.elemInvId)
          .map((e) => e.elemInvId);
        const allDeletedIds = tobeDeletedIds.concat(childrenIds);

        for (const id of allDeletedIds ?? []) {
          const item = elementsValue.value.inspectionElementPons.find((item) => item.elemInvId === id);
          item.deleted = true;
        }

        convertToHierarchy();
        changesDetected.value = true;
      }
    }

    function openElementConditionDialog(element) {
      dialog.value = true;
      selectedElementToShowInfo.value = element;
    }

    function updateDefectSelection(defect) {
      const selectedIndex = tempSelectedElemInspections.findIndex((d) => d.elemInvId == defect.elemInvId);
      if (defect.isSelected) {
        defect.isSelected = false;
        clearElementValues(defect, true);
        if (selectedIndex > -1) {
          if(elementsValue.value.inspectionElementPons.some((b) => b.elemInvId === defect.elemInvId)){
            tempSelectedElemInspections[selectedIndex].deleted = true;
          } else {
            tempSelectedElemInspections.splice(selectedIndex, 1);
          }
          
        }
        modifyElementForm.value.validate();
      } else {
        if (selectedIndex > -1) {
          tempSelectedElemInspections[selectedIndex].deleted = false;
        }
        defect.isSelected = true;
        defect.elemParentKey = selectedElement.value.elemKey;
        defect.elemParentId = selectedElement.value.elemInvId;
        defect.elemScaleFactor = "1.0";
        defect.strUnitKey = selectedElement.value.strUnitKey;
        defect.brkey = elementsValue.value.structureBridge.brkey;
        defect.inspectionId = elementsValue.value.inspection.inspectionId;
        if (!defect.elemInvId) {
          defect.elemInvId = getNextInvId(tempSelectedElemInspections);
        }
        defect.elemDocRefKey = "";
        defect.updateDateTime = new Date().toISOString();
        defect.updateUserId = "";
        defect.elemTag = selectedElement.value.elemTag;
        defect.envKey = selectedElement.value.envKey;
        defect.elemDescription = "";
        defect.elemNotes = "";
        if (!tempSelectedElemInspections.some((d) => d.elemInvId === defect.elemInvId)) {
          tempSelectedElemInspections.push(defect);
        }
      }
      defect.isModified = true;
    }
    function getInspectionElementObject(value) {
      return {
        brkey: elementsValue.value.structureBridge.brkey,
        inspectionId: elementsValue.value.inspection.inspectionId,
        elemInvId: value?.elemInvId,
        elemDescription: value?.elemDescription,
        elemDocRefKey: value.elemDocRefKey || "",
        elemGrandparentKey: value.elemGrandparentKey || 0,
        elemKey: value.elemKey,
        updateDateTime: value.updateDateTime || new Date().toISOString(),
        updateUserId: value.updateUserId || "",
        elemNotes: value.elemNotes,
        elemParentId: value.elemParentId || 0,
        elemParentKey: value?.elemParentKey,
        elemPctState1: convertToFloat(value.elemPctState1, 0),
        elemPctState2: convertToFloat(value.elemPctState2, 0),
        elemPctState3: convertToFloat(value.elemPctState3, 0),
        elemPctState4: convertToFloat(value.elemPctState4, 0),
        elemQtyState1: convertToFloat(value.elemQtyState1, 0),
        elemQtyState2: convertToFloat(value.elemQtyState2, 0),
        elemQtyState3: convertToFloat(value.elemQtyState3, 0),
        elemQtyState4: convertToFloat(value.elemQtyState4, 0),
        elemQuantity: convertToFloat(value.elemQuantity),
        elemScaleFactor: convertToFloat(value.elemScaleFactor),
        elemTag: value?.elemTag,
        envKey: value?.envKey,
        strUnitKey: selectedElement.value.strUnitKey,
      };
    }

    function editElementCount(element) {
      if (element) {
        let editedElement = tempSelectedElemInspections.find((d) => d.elemInvId == element.elemInvId);
        updateElementCounts(editedElement, element);
        //For sub components update protective systems count
        if (element.elemGrandparentKey && element.elemParentKey) {
          let protectiveSystem = tempSelectedElemInspections.find(
            (d) => d.elemKey == element.elemParentKey && d.elemInvId == element.elemParentId,
          );
          //recalculate protective system counts
          let sysWithNewCounts = allProtectiveSystems.value.find(
            (p) => p.elemKey == element.elemParentKey && p.elemInvId == element.elemParentId,
          );
          updateElementCounts(protectiveSystem, sysWithNewCounts);
        }
      }
    }
    function updateElementCounts(editedElement, element) {
      if (editedElement) {
        editedElement.elemQuantity = convertToFloat(element.elemQuantity);
        editedElement.elemQtyState1 = convertToFloat(element.elemQtyState1);
        editedElement.elemQtyState2 = convertToFloat(element.elemQtyState2);
        editedElement.elemQtyState3 = convertToFloat(element.elemQtyState3);
        editedElement.elemQtyState4 = convertToFloat(element.elemQtyState4);
        editedElement.elemPctState1 = element.elemPctState1;
        editedElement.elemPctState2 = element.elemPctState2;
        editedElement.elemPctState3 = element.elemPctState3;
        editedElement.elemPctState4 = element.elemPctState4;
        editedElement.elemScaleFactor = element.elemScaleFactor;
      }
    }

    function updateSubComponentCount(subcomp) {
      for (const a of allSubComponents.value ?? []) {
        updateSubCompCount(a);
      }
      editElementCount(subcomp);
    }

    function updateSubCompCount(a) {
      let totalElemQuantity = convertToInt(a.elemQuantity);
      if (updateElementQtyGroup.value === ELEMENTS.PERCENTAGE) {
        //When percentage is selected
        if (totalElemQuantity > 0) {
          a.elemQtyState2 = convertFromPercent(a.elemPctState2, totalElemQuantity);
          a.elemQtyState3 = convertFromPercent(a.elemPctState3, totalElemQuantity);
          a.elemQtyState4 = convertFromPercent(a.elemPctState4, totalElemQuantity);
        }
      }
      if (a.elemQuantity > 0) {
        calculatePercentages(a);
        a.elemPctState1 = convertToDecimalString(
          100 -
            (convertToFloat(a.elemPctState2, 0) +
              convertToFloat(a.elemPctState3, 0) +
              convertToFloat(a.elemPctState4, 0)),
          2,
        );
        a.elemQtyState1 =
          a.elemQuantity -
          (convertToInt(a.elemQtyState2) + convertToInt(a.elemQtyState3) + convertToInt(a.elemQtyState4));
      }
    }

    function updateCount(a) {
      if (updateElementQtyGroup.value === ELEMENTS.QUANTITY) {
        a.elemQuantity =
          !a.elemQtyState2 && !a.elemQtyState3 && !a.elemQtyState4
            ? null
            : convertToInt(a.elemQtyState2) + convertToInt(a.elemQtyState3) + convertToInt(a.elemQtyState4);
      } else {
        //When percentage is selected
        let totalElemQuantity = convertToInt(a.elemQuantity);

        if (totalElemQuantity > 0) {
          a.elemQtyState2 = convertFromPercent(a.elemPctState2, totalElemQuantity);
          a.elemQtyState3 = convertFromPercent(a.elemPctState3, totalElemQuantity);
          a.elemQtyState4 = convertFromPercent(a.elemPctState4, totalElemQuantity);
        }
      }
      if (a.elemQuantity > 0) {
        calculatePercentages(a);
        a.elemPctState1 = convertToDecimalString(
          100 -
            (convertToFloat(a.elemPctState2, 0) +
              convertToFloat(a.elemPctState3, 0) +
              convertToFloat(a.elemPctState4, 0)),
          2,
        );
        a.elemQtyState1 =
          a.elemQuantity -
          (convertToInt(a.elemQtyState2) + convertToInt(a.elemQtyState3) + convertToInt(a.elemQtyState4));
      }
    }

    function updateDefectCount(defect) {
      let totalCS2Qty = 0;
      let totalCS3Qty = 0;
      let totalCS4Qty = 0;
      let totalCS2Pct = 0;
      let totalCS3Pct = 0;
      let totalCS4Pct = 0;

      for (const a of allDefects.value ?? []) {
        updateCount(a);
        totalCS2Qty = totalCS2Qty + convertToInt(a.elemQtyState2);
        totalCS3Qty = totalCS3Qty + convertToInt(a.elemQtyState3);
        totalCS4Qty = totalCS4Qty + convertToInt(a.elemQtyState4);

        totalCS2Pct = totalCS2Pct + convertToFloat(a.elemPctState2, 0);
        totalCS3Pct = totalCS3Pct + convertToFloat(a.elemPctState3, 0);
        totalCS4Pct = totalCS4Pct + convertToFloat(a.elemPctState4, 0);
      }

      selectedElement.value.elemQtyState1 =
        convertToInt(selectedElement.value.elemQuantity) - (totalCS2Qty + totalCS3Qty + totalCS4Qty);
      selectedElement.value.elemQtyState2 = totalCS2Qty;
      selectedElement.value.elemQtyState3 = totalCS3Qty;
      selectedElement.value.elemQtyState4 = totalCS4Qty;

      selectedElement.value.elemPctState1 = convertToPercent(
        selectedElement.value.elemQtyState1,
        selectedElement.value.elemQuantity,
      );
      selectedElement.value.elemPctState2 = convertToPercent(
        selectedElement.value.elemQtyState2,
        selectedElement.value.elemQuantity,
      );
      selectedElement.value.elemPctState3 = convertToPercent(
        selectedElement.value.elemQtyState3,
        selectedElement.value.elemQuantity,
      );
      selectedElement.value.elemPctState4 = convertToPercent(
        selectedElement.value.elemQtyState4,
        selectedElement.value.elemQuantity,
      );
      editElementCount(defect);
    }

    function setProtectiveSystemCount(c) {
      if (updateElementQtyGroup.value === ELEMENTS.QUANTITY) {
        c.elemQuantity =
          !c.elemQtyState2 && !c.elemQtyState3 && !c.elemQtyState4
            ? null
            : convertToInt(c.elemQtyState2) + convertToInt(c.elemQtyState3) + convertToInt(c.elemQtyState4);
      } else {
        //When percentage is selected
        let totalElemQuantity = convertToInt(c.elemQuantity);
        if (totalElemQuantity > 0) {
          c.elemQtyState2 = convertFromPercent(c.elemPctState2, totalElemQuantity);
          c.elemQtyState3 = convertFromPercent(c.elemPctState3, totalElemQuantity);
          c.elemQtyState4 = convertFromPercent(c.elemPctState4, totalElemQuantity);
        }
      }
    }

    function updateProtectiveSystemCount(subSystem) {
      for (const p of allProtectiveSystems.value ?? []) {
        let totalCS2Qty = 0;
        let totalCS3Qty = 0;
        let totalCS4Qty = 0;
        let totalCS2Pct = 0;
        let totalCS3Pct = 0;
        let totalCS4Pct = 0;

        for (const c of p.SUB_COMPS ?? []) {
          setProtectiveSystemCount(c);
          totalCS2Qty = totalCS2Qty + convertToInt(c.elemQtyState2);
          totalCS3Qty = totalCS3Qty + convertToInt(c.elemQtyState3);
          totalCS4Qty = totalCS4Qty + convertToInt(c.elemQtyState4);

          if (c.elemQuantity > 0) {
            calculatePercentages(c);
            totalCS2Pct = totalCS2Pct + convertToFloat(c.elemPctState2, 0);
            totalCS3Pct = totalCS3Pct + convertToFloat(c.elemPctState3, 0);
            totalCS4Pct = totalCS4Pct + convertToFloat(c.elemPctState4, 0);
            c.elemPctState1 = convertToDecimalString(
              100 -
                (convertToFloat(c.elemPctState2, 0) +
                  convertToFloat(c.elemPctState3, 0) +
                  convertToFloat(c.elemPctState4, 0)),
              2,
            );
            c.elemQtyState1 =
              c.elemQuantity -
              (convertToInt(c.elemQtyState2) + convertToInt(c.elemQtyState3) + convertToInt(c.elemQtyState4));
          }
        }

        p.elemQtyState1 = convertToInt(p.elemQuantity) - (totalCS2Qty + totalCS3Qty + totalCS4Qty);

        p.elemQtyState2 = totalCS2Qty;
        p.elemQtyState3 = totalCS3Qty;
        p.elemQtyState4 = totalCS4Qty;

        p.elemPctState1 = convertToPercent(p.elemQtyState1, p.elemQuantity);
        p.elemPctState2 = convertToPercent(p.elemQtyState2, p.elemQuantity);
        p.elemPctState3 = convertToPercent(p.elemQtyState3, p.elemQuantity);
        p.elemPctState4 = convertToPercent(p.elemQtyState4, p.elemQuantity);
      }
      editElementCount(subSystem);
    }

    const calculatePercentages = (a) => {
      a.elemPctState2 = convertToPercent(a.elemQtyState2, a.elemQuantity);
      a.elemPctState3 = convertToPercent(a.elemQtyState3, a.elemQuantity);
      a.elemPctState4 = convertToPercent(a.elemQtyState4, a.elemQuantity);
    };

    function updateProtectiveSystemSelection(protectiveSystem) {
      if (protectiveSystem.isSelected) {
        markProtectiveSystemAsDeleted(protectiveSystem);
      } else {
        markProtectiveSystemAsSelected(protectiveSystem);
      }
      // Either way, mark the entry as modified
      protectiveSystem.isModified = true;
    }

    function markProtectiveSystemAsDeleted(protectiveSystem) {
      const selectedIndex = tempSelectedElemInspections.findIndex((p) => p.elemInvId == protectiveSystem.elemInvId);
      for (const element of protectiveSystem.SUB_COMPS ?? []) {
        element.isSelected = false;
        clearElementValues(element, false);
        const index = tempSelectedElemInspections.findIndex((p) => p.elemInvId == element.elemInvId);
        if (index > -1) {
          tempSelectedElemInspections[index].deleted = true;
        }
      }
      protectiveSystem.isSelected = false;
      clearElementValues(protectiveSystem, false);

      if (selectedIndex > -1) {
        if(elementsValue.value.inspectionElementPons.some((b) => b.elemInvId === protectiveSystem.elemInvId)){
          tempSelectedElemInspections[selectedIndex].deleted = true;
        }else{
          tempSelectedElemInspections.splice(selectedIndex, 1);
        }
      }
      modifyElementForm.value.validate();
    }

    function markProtectiveSystemAsSelected(protectiveSystem) {
      const selectedIndex = tempSelectedElemInspections.findIndex((p) => p.elemInvId == protectiveSystem.elemInvId);
      if (selectedIndex > -1) {
        tempSelectedElemInspections[selectedIndex].deleted = false;
      }
      protectiveSystem.isSelected = true;
      if (!protectiveSystem.elemInvId) {
        protectiveSystem.elemInvId = getNextInvId(tempSelectedElemInspections);
      }
      protectiveSystem.strUnitKey = selectedElement.value.strUnitKey;
      if (protectiveSystem.SUB_COMPS) {
        //Add parent id for protective systems
        protectiveSystem.elemParentId = selectedElement.value.elemInvId;
      } else {
        //get the inv_id of the protective system
        const parent = tempSelectedElemInspections.find(
          (p) => p.elemKey == protectiveSystem.elemParentKey && p.elemParentId == selectedElement.value.elemInvId,
        );
        //Add parentid for subcomponents under protective system
        protectiveSystem.elemParentId = parent ? parent.elemInvId : 0;
      }
      protectiveSystem.brkey = elementsValue.value.structureBridge.brkey;
      protectiveSystem.inspectionId = elementsValue.value.inspection.inspectionId;

      protectiveSystem.updateDateTime = new Date().toISOString();
      protectiveSystem.updateUserId = "";
      protectiveSystem.elemDocRefKey = "";
      protectiveSystem.elemScaleFactor = "1.0";
      protectiveSystem.elemTag = selectedElement.value.elemTag;
      protectiveSystem.envKey = selectedElement.value.envKey;
      protectiveSystem.elemDescription = "";
      protectiveSystem.elemNotes = "";
      if (!tempSelectedElemInspections.some((e) => e.elemInvId === protectiveSystem.elemInvId)) {
        tempSelectedElemInspections.push(protectiveSystem);
      }
    }

    
    function clearElementValues(element, isDefect) {
      element.elemScaleFactor = "1.0";
      element.elemQuantity = 0;
      element.elemQtyState1 = 0;
      element.elemQtyState2 = 0;
      element.elemQtyState3 = 0;
      element.elemQtyState4 = 0;
      element.elemPctState1 = 0;
      element.elemPctState2 = 0;
      element.elemPctState3 = 0;
      element.elemPctState4 = 0;
      if (isDefect) {
        updateDefectCount(null);
      } else {
        updateProtectiveSystemCount(null);
        //update parent protective system count
        editElementCount(element);
      }
    }

    function convertToInspectionElements() {
      //convert modified protective systems and defects to inspection element object
      for (const [i, inspElement] of (tempSelectedElemInspections ?? []).entries()) {
        if (inspElement.isModified) {
          tempSelectedElemInspections[i] = getInspectionElementObject(inspElement);
        }
      }
      elementsValue.value.inspectionElementPons = clone(tempSelectedElemInspections);
      let index = elementsValue.value.inspectionElementPons.findIndex(
        (a) => a.elemInvId == selectedElement.value.elemInvId,
      );

      if (index > -1) {
        elementsValue.value.inspectionElementPons[index] = getInspectionElementObject(selectedElement.value);
      } else {
        elementsValue.value.inspectionElementPons.push(getInspectionElementObject(selectedElement.value));
      }
      convertToHierarchy();
    }

    //when cancel is clicked on Add element dialog
    function resetInspectionChanges() {
      convertToHierarchy();
    }

    function copyElementSave() {
      let inspectionElementsectionsToBeCopied = elementsValue.value?.inspectionElementPons
        .filter((a) => a.strUnitKey == selectedCopyElementSourceStructureUnitId.value)
        .filter(
          (b) =>
            b.elemKey == selectedCopyElementElementId.value ||
            b.elemParentKey == selectedCopyElementElementId.value ||
            b.elemGrandparentKey == selectedCopyElementElementId.value,
        );
      setCopiedElementIds(inspectionElementsectionsToBeCopied, selectedCopyElementTargetStructureUnitId.value);
      convertToHierarchy();
      resetSelections();
    }

    function copyStructureUnitSave() {
      let elementsToBeCopied = elementsValue.value?.inspectionElementPons.filter(
        (a) => a.strUnitKey == selectedCopyStructureUnitSourceId.value,
      );
      setCopiedElementIds(elementsToBeCopied, selectedCopyStructureUnitTargetId.value);
      convertToHierarchy();
      resetSelections();
    }

    const setCopiedElementIds = (elementsToBeCopied, targetStrUnitKey) => {
      let oldNewIds = new Map();
      let tempCopiedArr = [];
      let index = getNextInvId(elementsValue.value?.inspectionElementPons);
      for (const a of elementsToBeCopied ?? []) {
        let copiedInspectionElement = clone(a);
        copiedInspectionElement.strUnitKey = targetStrUnitKey;
        const oldId = copiedInspectionElement.elemInvId;
        copiedInspectionElement.elemInvId = index++;
        oldNewIds.set(oldId, copiedInspectionElement.elemInvId);
        tempCopiedArr.push(copiedInspectionElement);
      }

      for (const a of tempCopiedArr ?? []) {
        if (a.elemParentId) {
          const newId = oldNewIds.get(a.elemParentId);
          a.elemParentId = newId;
        }
        elementsValue.value?.inspectionElementPons.push(a);
      }
      changesDetected.value = true;
    };

    function validateElementForm() {
      //form validations
      modifyElementForm.value
        ?.validate()
        .then(() => {
          if (valid.value) {
            inspectionStore.setShowValidationSnackbar(false);
            if (noFunctionalSystemMessage.value) {
              inspectionStore.setShowValidationSnackbar(true);
            } else {
              convertToInspectionElements();
              showElementDialog.value.show = false;
              nextTick(() => {
                inspectionLinksStore.isDirty = true;
              });
            }
          } else {
            inspectionStore.setShowValidationSnackbar(true);
          }
        })
        .catch((err) => LOGGER.logException(err));
    }

    function closeUpdateDialog() {
      noFunctionalSystemMessage.value = null;
      if (!isEditable.value || !changesDetected.value) {
        showElementDialog.value.show = false;
      } else {
        //show confirmation dialog on closing
        closeConfirm.value
          .open(INSPECTION_MESSAGES.DETECT_POPUP_CHANGE_CONFIRMATION_MESSAGE, BUTTONS.YES)
          .then((response) => {
            if (response) {
              showElementDialog.value.show = false;
              resetInspectionChanges();
            }
          });
      }
    }

    const getNextInvId = (inspectionElements) => {
      return Math.max(...inspectionElements.map((a) => a.elemInvId), 0) + 1;
    };

    const getUniqueElements = (value) => {
      let returnedElements = [];
      if (value) {
        const addedElements = structureUnitGroups.value[selectedElement.value.strUnitKey]?.elements
          .filter((a) => a.elemGrandparentKey == 0 && a.elemParentKey == 0)
          .map((b) => b.elemKey);
        const structureType = getStructureType(elementsValue.value.structureBridge?.servTypOn);

        //remove already added elements from the list
        returnedElements = configStore
          .getAddElementDefinitions(structureType)
          .filter((f) => {
            if (
              structureType === STRUCTURE_TYPE.TUNNEL &&
              structureUnitGroups.value[selectedElement?.value.strUnitKey]?.legacyStructureType ===
                FUNCTIONAL_SYSTEM.STR_UNIT_TYPE
            ) {
              return f.elemMatKey === FUNCTIONAL_SYSTEM.MAT_KEY;
            } else if (structureType === STRUCTURE_TYPE.TUNNEL) {
              return f.elemMatKey != FUNCTIONAL_SYSTEM.MAT_KEY;
            } else {
              //bridge type
              return true;
            }
          })
          .map((e) => ({
            value: e.elemKey,
            title: `${e.elemKey} - ${e.elemShortName}`,
          }))
          .sort((a, b) => {
            return a.value - b.value;
          });
        for (const a of addedElements ?? []) {
          const index = returnedElements.findIndex((e) => e.value == a);
          if (index > -1 && selectedElement.value.elemKey != a) {
            returnedElements.splice(index, 1); // remove one item only
          }
        }
      }
      return returnedElements;
    };

    const getIcon = (elemDef) => {
      if (elemDef?.elemProtectSys === "Y") {
        return "fa-solid fa-shield no-outline";
      } else if (elemDef?.elemSubComp === "Y") {
        return "fa-solid fa-puzzle-piece no-outline";
      } else if (elemDef?.elemSmartFlag === "Y") {
        return "fa-solid fa-bolt-lightning no-outline";
      } else {
        //use this class to add action elements for functional systems.
        return "fa-solid no-outline";
      }
    };

    const getColor = (elemDef) => {
      if (elemDef?.elemProtectSys === "Y") {
        return "protective-system";
      } else if (elemDef?.elemSubComp === "Y") {
        return "subcomponent";
      } else if (elemDef?.elemSmartFlag === "Y") {
        return "defect";
      } else {
        return "element";
      }
    };

    const showInspectionPerformedMessage = () => {
      snackBarId.value = snackbarStore.showMessage({
        displayText:
          "7A06 Element Inspection Performed Box has been checked because changes have been made to the Elements screen.",
        timeout: 10000,
        messageType: SNACKBAR_MESSAGE_TYPES.SUCCESS,
      });
    };

    const toggleAllPanels = (expandAll) => {
      if (expandAll) {
        panel.value = allPanels;
      } else {
        panel.value = [];
      }
    };
    const toggleAllPopupPanels = (expandAll) => {
      if (expandAll) {
        elementsPanel.value = ["defects", "protectiveSystems", "subComponents"];
      } else {
        elementsPanel.value = [];
      }
    };
    const resetElementsValue = () => {
      resetDirty.value = true;
      elementsValue.value = {};
      convertToHierarchy();
    };

    const getDefaultStructureGroup = () => {
      structureUnitGroups.value[0] = {
        brkey: "",
        strUnitKey: "",
        strUnitLabel: "",
        legacyStructureType: "",
        elements: [],
        tempElements: [],
      };
      panel.value.push("structureUnit_0");
      allPanels.push("structureUnit_0");
    };

    const readData = async () => {
      elementsOpened.value = [];
      protectiveSystemselementsOpened.value = [];
      functionalElementsOpened.value = [];
      elementsValue.value = {};
      resetDirty.value = true;
      if (
        props.moduleType === MODULE_TYPES.MANAGEMENT &&
        quickSearchHeader.value?.BRKEY &&
        quickSearchHeader.value?.INSPECTIONID
      ) {
        if (!hasQuickSearchSucceeded.value) {
          resetDirty.value = true;
          convertToHierarchy();
          return;
        }
        await elementsStore.loadElementsObject(
          props.moduleType,
          {
            ...quickSearchHeader.value,
          },
          null,
        );
        elementsValue.value = elementsStore.getElementsObject(props.moduleType);
      } else if (props.moduleType === MODULE_TYPES.INSPECTION) {
        const structure = computed(() => inspectionStore.selectedInspection);
        await elementsStore.loadElementsObject(props.moduleType, null, structure.value);
        elementsValue.value = elementsStore.getElementsObject();
      }
      resetDirty.value = true;
      convertToHierarchy();
    };

    const saveChanges = async () => {
      inspectionStore.setShowValidationSnackbar(false);
      const structure = inspectionStore.selectedInspection;
      const isSuccess = await elementsStore.saveElements(props.moduleType, elementsValue.value, structure, {
        ...quickSearchHeader.value,
      });
      if (isSuccess) {
        inspectionLinksStore.isDirty = false;
        await readData();
      }
      return isSuccess;
    };

    const cancelChanges = async () => {
      //only called from Inspection Links
      elementsValue.value = elementsStore.getElementsObject(props.moduleType);
      // Using this flag to not set the dirty flag in watch
      resetDirty.value = true;
      inspectionLinksStore.isDirty = false;
      convertToHierarchy();
    };

    onMounted(async () => {
      if (inspectionPage.value != INSPECTION_PAGES.TUNNELS) {
        await readData();
      }
    });
    const printD491 = () => {
      return { reportPage: "D491-Insp-Elements-Screen", marker: SNACKBAR_MARKER.ELEMENTS };
    };
    watch(
      [allDefects, allProtectiveSystems],
      () => {
        if (!initialized.value) return;
        const defectsAndProtectivesCurrentState = {
          defects: allDefects.value,
          protectiveSystems: allProtectiveSystems.value,
        };
        const elementsFormChanged =
          JSON.stringify(defectsAndProtectivesCurrentState) !==
          JSON.stringify(defectsAndProtectivesOriginalState.value);
        changesDetected.value = elementsFormChanged;
        nextTick(() => {
          modifyElementForm.value.validate();
        });
      },
      { deep: true },
    );
    watch(
      () => showElementDialog.value.show,
      async (isOpen) => {
        if (isOpen) {
          initialized.value = false;
          changesDetected.value = false;
          await nextTick();
          defectsAndProtectivesOriginalState.value = {
            defects: clone(allDefects.value),
            protectiveSystems: clone(allProtectiveSystems.value),
          };
          initialized.value = true;
        } else {
          initialized.value = false;
          changesDetected.value = false;
        }
      },
    );
    // watch those objects used in component from structure to warn about unsaved changes
    watch(
      [elementsValue],
      () => {
        // This watch will NOT be triggered during initial onMounted()
        if (props.moduleType === MODULE_TYPES.MANAGEMENT) {
          // for management module
          if (resetDirty.value) {
            inspectionLinksStore.isDirty = false;
            resetDirty.value = false;
          } else {
            inspectionLinksStore.isDirty = true;
          }
        } else if (props.moduleType === MODULE_TYPES.INSPECTION) {
          // for inspection  module
          if (resetDirty.value) {
            inspectionStore.setDirtyFlag(false);
            resetDirty.value = false;
          } else {
            inspectionStore.setDirtyFlag(true);
          }
        }
      },
      { deep: true },
    );

    watch(
      () => [props],
      () => {
        readData();
      },
      { deep: true },
    );

    watch(updateElementQtyGroup, () => {
      nextTick(() => {
        modifyElementForm.value.validate();
      });
    });

    return {
      resetElementsValue,
      readData,
      saveChanges,
      cancelChanges,
      resetAddElementForUnitKeyChange,
      copyElementForm,
      toggleAllPopupPanels,
      getColor,
      quantityCountMaxLength,
      addElement,
      panel,
      elementQtyGroup,
      elementsOpened,
      toggle,
      editUnit,
      deleteUnit,
      showElementDialog,
      selectedElement,
      protectiveSystemselementsOpened,
      toggleProtectiveSystems,
      elemDescriptionMaxLength,
      elemConditionMaxLength,
      elementsPanel,
      structureUnitGroups,
      configStore,
      ELEMENTS,
      structureUnitTypes,
      allProtectiveSystems,
      allDefects,
      allSubComponents,
      dialog,
      openElementConditionDialog,
      selectedElementToShowInfo,
      updateDefectSelection,
      updateProtectiveSystemSelection,
      getElemConditionLegalText,
      getDefectsAndProtectiveSystems,
      updateElementQtyGroup,
      showCopyElementDialog,
      copyElement,
      showCopyStructureUnitDialog,
      copyStructureUnit,
      sourceStructureUnits,
      targetCopyStructureUnits,
      selectedCopyElementSourceStructureUnitId,
      selectedCopyElementElementId,
      selectedCopyElementTargetStructureUnitId,
      selectedCopyStructureUnitSourceId,
      selectedCopyStructureUnitTargetId,
      getElements,
      sourceElemsArr,
      targetCopyElementStructureUnits,
      setTargetUnits,
      resetSelections,
      convertToInspectionElements,
      resetInspectionChanges,
      copyElementSave,
      copyStructureUnitSave,
      updateDefectCount,
      updateProtectiveSystemCount,
      convertToFloat,
      convertToDecimalString,
      getStructureType,
      elementsValue,
      requiredRule,
      modifyElementForm,
      valid,
      validateElementForm,
      greaterThanZeroRule,
      changesDetected,
      closeUpdateDialog,
      closeConfirm,
      BUTTONS,
      REFERENCE_TABLE,
      validCopyElementForm,
      validCopyStructureUnitForm,
      inspectionStore,
      convertToHierarchy,
      isEditable,
      lessThanValueRule,
      elementOneQuantityMustBeGreaterThanZero,
      ERROR_MESSAGES,
      getUniqueElements,
      updateSubComponentCount,
      getIcon,
      noFunctionalSystemMessage,
      toggleFunctionalElements,
      functionalElementsOpened,
      systemElementMessage,
      INSPECTION_PAGES,
      showInspectionPerformedMessage,
      toggleAllPanels,
      greaterThanZeroRuleExclusive,
      elementTotalPercentEqualToOneHundredRule,
      quantityRules,
      printD491,
      isManagement,
    };
  },
  components: {
    NumberField,
    ConfirmDialog,
    InputMessage,
    InspectionElementRow,
    LabelText,
    LabelSelect,
    ExpandCollapseToggle,
    ValidationCountsHeader,
  },
};
</script>
<style scoped lang="scss">
@use "@/styles/colors" as c;

h3 {
  color: c.$p-white !important;
}

.flex-center {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.compactCell {
  width: 10%;
  padding: 0px !important;
}

.protective-system {
  background-color: c.$e-green;
}

.subcomponent {
  background-color: c.$e-yellow;
}

.defect {
  background-color: c.$e-red;
}
.equipTable :deep(.v-label) {
  font-size: 0.71rem;
  display: flex !important;
  align-items: center;
}

.element {
  background-color: c.$e-blue;
}

.elementsTable :deep(input.v-field__input) {
  padding: 5px !important;
}
</style>
